(function () {
  'use strict';

  const theme = window.theme || {};
  const settings = theme.settings || {};

  /* ---------- utilities ---------- */
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function currencyDecimalPlaces(code) {
    const upper = String(code || '').toUpperCase();
    // ISO currencies with non-2 minor units that Shopify stores commonly use.
    if (['KWD', 'BHD', 'OMR', 'JOD'].includes(upper)) return 3;
    if (['JPY', 'KRW', 'VND', 'CLP', 'XOF', 'XAF'].includes(upper)) return 0;
    return 2;
  }

  function toMajorUnits(value) {
    if (value == null || value === '') return 0;
    const raw = typeof value === 'string' ? value.trim().replace(/,/g, '') : value;
    const n = Number(raw);
    if (!Number.isFinite(n)) return 0;
    // Predictive search returns decimal strings in major units ("12.500").
    // Cart/Ajax integers are Shopify cents (always ÷ 100).
    if (typeof raw === 'string' && /[.]/.test(raw)) return n;
    if (!Number.isInteger(n)) return n;
    return n / 100;
  }

  function formatMoney(value) {
    const currency = settings.currency || 'KWD';
    const decimals = currencyDecimalPlaces(currency);
    const amount = toMajorUnits(value);
    const lang = document.documentElement.lang || 'en';

    try {
      return new Intl.NumberFormat(lang, {
        style: 'currency',
        currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(amount);
    } catch (err) {
      /* fall through */
    }

    const format = settings.moneyFormat || '{{amount}} KD';
    const formatted = amount.toFixed(decimals);
    const withSeparators = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return String(format)
      .replace(/\{\{\s*amount(?:_with_comma_separator)?\s*\}\}/g, withSeparators)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(amount).toLocaleString(lang))
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, withSeparators.replace(/,/g, ' '))
      .replace(/\{\{\s*amount[^}]*\}\}/g, withSeparators);
  }

  function trapFocus(container, options = {}) {
    const focusable = qsa(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      container
    ).filter((el) => {
      if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true' || el.hidden) return false;
      if (el.closest('[hidden], [aria-hidden="true"], .visually-hidden')) return false;
      if (el.tabIndex < 0) return false;
      return true;
    });
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const preferred = qs('[data-autofocus]', container);
    function onKey(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', onKey);
    if (!options.skipInitialFocus) {
      (preferred || first).focus();
    }
    return () => container.removeEventListener('keydown', onKey);
  }

  function lockScroll(lock) {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
  }

  /* ---------- drawer / modal controller ---------- */
  const openCleanups = new Map();

  function syncOverlayState() {
    const anyOpen = qsa('.drawer.is-open, .modal.is-open, .mobile-menu.is-open').length > 0;
    document.documentElement.classList.toggle('overlay-open', anyOpen);
    document.body.classList.toggle('overlay-open', anyOpen);
  }

  /* Keep overlay-open in sync if is-open is toggled outside openOverlay/closeOverlay */
  const overlayRoots = qsa('.drawer, .modal, .mobile-menu');
  if (overlayRoots.length && typeof MutationObserver !== 'undefined') {
    const overlayObserver = new MutationObserver(() => syncOverlayState());
    overlayRoots.forEach((el) => {
      overlayObserver.observe(el, { attributes: true, attributeFilter: ['class'] });
    });
  }

  function getSwipePanel(root) {
    if (!root) return null;
    return (
      qs(
        '.delivery-sheet__panel, .cart-drawer__panel, .gift-sheet__panel, .checkout-upsell__dialog, .qv-dialog, .drawer__panel',
        root
      ) || null
    );
  }

  function getSwipeScroller(root) {
    if (!root) return null;
    return (
      qs(
        '.delivery-sheet__body, .cart-drawer__body, .gift-sheet__body, .checkout-upsell__body, .qv__scroll, .drawer__body',
        root
      ) || null
    );
  }

  function isSwipeableSheet(root) {
    if (!root || !root.classList.contains('is-open')) return false;
    if (root.classList.contains('drawer--bottom')) {
      // Desktop centered dialogs should not swipe-dismiss
      if (window.matchMedia('(min-width: 750px)').matches) {
        if (root.classList.contains('delivery-sheet') || root.classList.contains('checkout-upsell')) {
          return false;
        }
      }
      return true;
    }
    // Mobile cart acts as a bottom sheet
    if (root.id === 'CartDrawer' && window.matchMedia('(max-width: 749px)').matches) return true;
    // Quick view is a bottom sheet on phones/tablets
    if (root.id === 'QuickViewModal' && window.matchMedia('(max-width: 859px)').matches) {
      if (root.classList.contains('qv-lightbox-open')) return false;
      return true;
    }
    return false;
  }

  function canBeginSheetSwipe(root, target) {
    if (!target || !root) return false;
    if (target.closest('[data-swipe-handle]')) return true;
    if (target.closest('[data-qv-lightbox]:not([hidden])')) return false;
    if (
      target.closest(
        'input, textarea, select, button, a, label, [role="option"], [data-delivery-day], [data-delivery-slot], [data-cal-day], [data-cal-date], [data-cal-nav], [data-field-tip], [data-sender-tip]'
      )
    ) {
      return false;
    }
    if (
      target.closest(
        '.delivery-sheet__header, .cart-drawer__header, .gift-sheet__header, .checkout-upsell__header, .drawer__header, .qv-dialog__header, .delivery-sheet__handle, .cart-drawer__handle, .gift-sheet__handle, .checkout-upsell__handle, .drawer__handle, .qv-dialog__handle'
      )
    ) {
      return true;
    }
    const scroller = getSwipeScroller(root);
    return !scroller || scroller.scrollTop <= 0;
  }

  let sheetSwipe = null;

  function endSheetSwipe(shouldClose) {
    if (!sheetSwipe) return;
    const { root, panel, overlay } = sheetSwipe;
    sheetSwipe = null;
    if (!panel) return;
    panel.classList.remove('is-swiping');
    if (shouldClose) {
      panel.style.transition = 'transform 0.22s ease-out';
      panel.style.transform = 'translateY(110%)';
      if (overlay) {
        overlay.style.transition = 'opacity 0.22s ease-out';
        overlay.style.opacity = '0';
      }
      window.setTimeout(() => {
        panel.style.transform = '';
        panel.style.transition = '';
        if (overlay) {
          overlay.style.opacity = '';
          overlay.style.transition = '';
        }
        closeOverlay(root);
      }, 200);
      return;
    }
    panel.style.transition = 'transform 0.22s ease-out';
    panel.style.transform = '';
    if (overlay) {
      overlay.style.transition = 'opacity 0.22s ease-out';
      overlay.style.opacity = '';
    }
    window.setTimeout(() => {
      panel.style.transition = '';
      if (overlay) overlay.style.transition = '';
    }, 220);
  }

  document.addEventListener(
    'touchstart',
    (e) => {
      if (sheetSwipe || e.touches.length !== 1) return;
      const root = e.target.closest('.drawer.is-open, #QuickViewModal.is-open');
      if (!isSwipeableSheet(root)) return;
      if (!canBeginSheetSwipe(root, e.target)) return;
      const panel = getSwipePanel(root);
      if (!panel) return;
      sheetSwipe = {
        root,
        panel,
        overlay: qs('.drawer__overlay, .modal__overlay', root),
        startY: e.touches[0].clientY,
        startX: e.touches[0].clientX,
        dy: 0,
        active: false,
        fromHandle: Boolean(e.target.closest('[data-swipe-handle]')),
      };
    },
    { passive: true }
  );

  document.addEventListener(
    'touchmove',
    (e) => {
      if (!sheetSwipe || e.touches.length !== 1) return;
      const touch = e.touches[0];
      const dy = touch.clientY - sheetSwipe.startY;
      const dx = touch.clientX - sheetSwipe.startX;

      if (!sheetSwipe.active) {
        if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
          sheetSwipe = null;
          return;
        }
        if (dy < 6) return;
        if (!sheetSwipe.fromHandle) {
          const scroller = getSwipeScroller(sheetSwipe.root);
          if (scroller && scroller.scrollTop > 0) {
            sheetSwipe = null;
            return;
          }
        }
        sheetSwipe.active = true;
        sheetSwipe.panel.classList.add('is-swiping');
      }

      if (!sheetSwipe.active) return;
      e.preventDefault();
      const clamped = Math.max(0, dy);
      sheetSwipe.dy = clamped;
      sheetSwipe.panel.style.transform = `translateY(${clamped}px)`;
      if (sheetSwipe.overlay) {
        const fade = Math.max(0.25, 1 - clamped / 280);
        sheetSwipe.overlay.style.opacity = String(fade);
      }
    },
    { passive: false }
  );

  document.addEventListener(
    'touchend',
    () => {
      if (!sheetSwipe) return;
      const shouldClose = sheetSwipe.active && sheetSwipe.dy > 90;
      endSheetSwipe(shouldClose);
    },
    { passive: true }
  );

  document.addEventListener(
    'touchcancel',
    () => {
      if (!sheetSwipe) return;
      endSheetSwipe(false);
    },
    { passive: true }
  );

  function openOverlay(el) {
    if (!el) return;
    if (el.id === 'DeliverySheet') {
      const cartDrawer = qs('#CartDrawer');
      const quickView = qs('#QuickViewModal');
      el.dataset.resumeCart = cartDrawer?.classList.contains('is-open') ? '1' : '';
      el.dataset.resumeQuickView = quickView?.classList.contains('is-open') ? '1' : '';
    }
    // One overlay at a time — avoids focus leaking into filter price inputs, etc.
    // Gift addon picker can sit on top of the bundle builder.
    qsa('.drawer.is-open, .modal.is-open, .mobile-menu.is-open').forEach((openEl) => {
      if (openEl === el) return;
      if (el.id === 'GiftAddonSheet' && openEl.id === 'ProductBundleSheet') {
        el.dataset.resumeBundle = '1';
        const cleanup = openCleanups.get(openEl);
        if (cleanup) {
          cleanup();
          openCleanups.delete(openEl);
        }
        return;
      }
      closeOverlay(openEl);
    });
    el.hidden = false;
    el.setAttribute('aria-hidden', 'false');
    // Force a reflow so transform transitions run, then open.
    // Prefer this over rAF — mobile browsers can delay rAF while scrolling.
    void el.offsetWidth;
    el.classList.add('is-open');
    if (el.id === 'DeliverySheet') {
      el.dataset.deliveryConfirmed = '';
      const picker = qs('[data-cart-delivery]', el);
      if (picker?._captureCommittedDelivery) picker._captureCommittedDelivery();
    }
    if (el.id === 'CartDrawer') {
      ensureDefaultDelivery().catch(() => {});
    }
    syncOverlayState();
    lockScroll(true);
    const panel = qs('.drawer__panel, .modal__dialog, .mobile-menu__panel, .search-modal__dialog', el) || el;
    const autofocusEl = qs('[data-autofocus]', panel);

    // iOS: focusing during the open transition can scroll the input off-screen.
    if (el.id === 'SearchModal' && autofocusEl) {
      openCleanups.set(el, trapFocus(panel, { skipInitialFocus: true }));
      window.setTimeout(() => {
        if (!el.classList.contains('is-open')) return;
        autofocusEl.focus({ preventScroll: true });
        try {
          if (typeof autofocusEl.setSelectionRange === 'function' && autofocusEl.type !== 'number') {
            autofocusEl.setSelectionRange(autofocusEl.value.length, autofocusEl.value.length);
          }
        } catch (err) {
          /* input type=search may not support setSelectionRange in all browsers */
        }
      }, 340);
    } else {
      openCleanups.set(el, trapFocus(panel));
    }
    qsa(`[data-open="#${el.id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
  }

  function closeOverlay(el) {
    if (!el) return;
    let resumeCheckoutUrl = '';
    if (el.id === 'CheckoutUpsell') {
      // Closing the upsell must end the loop — never re-show it this session.
      markCheckoutUpsellDismissed();
      resumeCheckoutUrl = el.dataset.checkoutUrl || '';
      el.dataset.checkoutUrl = '';
    }
    const resumeCart = el.id === 'DeliverySheet' && el.dataset.resumeCart === '1';
    const resumeQuickView = el.id === 'DeliverySheet' && el.dataset.resumeQuickView === '1';
    const resumeBundle = el.id === 'GiftAddonSheet' && el.dataset.resumeBundle === '1';
    if (el.id === 'GiftAddonSheet') el.dataset.resumeBundle = '';
    if (el.id === 'DeliverySheet') {
      if (el.dataset.deliveryConfirmed !== '1') {
        const picker = qs('[data-cart-delivery]', el);
        if (picker?._revertDeliveryDraft) picker._revertDeliveryDraft();
        el.dataset.resumeCheckout = '';
      }
      el.dataset.resumeCart = '';
      el.dataset.resumeQuickView = '';
    }
    const panel = getSwipePanel(el);
    if (panel) {
      panel.classList.remove('is-swiping');
      panel.style.transform = '';
      panel.style.transition = '';
    }
    const overlay = qs('.drawer__overlay, .modal__overlay, .mobile-menu__overlay', el);
    if (overlay) overlay.style.opacity = '';
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    if (el.id === 'QuickViewModal') {
      const lightbox = qs('[data-qv-lightbox]', el);
      if (lightbox) {
        lightbox.hidden = true;
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
      }
      el.classList.remove('qv-lightbox-open');
    }
    syncOverlayState();
    const cleanup = openCleanups.get(el);
    if (cleanup) cleanup();
    openCleanups.delete(el);
    qsa(`[data-open="#${el.id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    setTimeout(() => {
      if (!el.classList.contains('is-open')) el.hidden = true;
      if (el.id === 'ProductBundleSheet' && el.dataset.comboSource === 'remote') {
        restoreNativeComboSheet();
      }
      if (!qsa('.drawer.is-open, .modal.is-open, .mobile-menu.is-open').length) {
        lockScroll(false);
        syncOverlayState();
      }
    }, 280);

    // Shopper clicked Checkout, then dismissed add-ons → take them to checkout.
    if (resumeCheckoutUrl) {
      window.setTimeout(() => {
        window.location.href = resumeCheckoutUrl;
      }, 160);
      return;
    }

    // Delivery sheet replaces the cart or quick view; put that overlay back after save or dismiss.
    if (resumeCart) {
      openOverlay(qs('#CartDrawer'));
    } else if (resumeQuickView) {
      openOverlay(qs('#QuickViewModal'));
    } else if (resumeBundle) {
      const bundle = qs('#ProductBundleSheet');
      if (bundle?.classList.contains('is-open')) {
        const bundlePanel = qs('.drawer__panel, .modal__dialog', bundle) || bundle;
        openCleanups.set(bundle, trapFocus(bundlePanel));
      }
    }

    if (
      el.id === 'ProductBundleSheet' &&
      typeof history !== 'undefined' &&
      history.replaceState &&
      (window.location.hash === '#ProductBundle' || window.location.hash === '#ProductBundleSheet')
    ) {
      history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  }

  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-open]');
    if (openBtn) {
      e.preventDefault();
      const closeSel = openBtn.getAttribute('data-close');
      if (closeSel) closeOverlay(qs(closeSel));
      const targetSel = openBtn.getAttribute('data-open');
      const target = qs(targetSel);
      if (target?.id === 'DeliverySheet') target.dataset.resumeCheckout = '';
      if (target?.id === 'ProductBundleSheet') {
        snapshotComboNotes();
        openProductBundleSheet();
        return;
      }
      openOverlay(target);
      return;
    }
    const closeBtn = e.target.closest('[data-close]');
    if (closeBtn) {
      const target = closeBtn.getAttribute('data-close');
      closeOverlay(target ? qs(target) : closeBtn.closest('.drawer, .modal, .mobile-menu'));
      // Allow real links to navigate (mobile menu items use data-close + href).
      if (closeBtn.matches('a[href]')) {
        const href = closeBtn.getAttribute('href');
        if (href && href !== '#' && !href.startsWith('javascript:')) {
          return;
        }
      }
      e.preventDefault();
      return;
    }
    if (e.target.matches('.drawer__overlay, .modal__overlay, .mobile-menu__overlay')) {
      closeOverlay(e.target.closest('.drawer, .modal, .mobile-menu'));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const giftPicker = qs('#GiftAddonSheet.is-open');
      if (giftPicker) {
        closeOverlay(giftPicker);
        return;
      }
      qsa('.drawer.is-open, .modal.is-open, .mobile-menu.is-open').forEach(closeOverlay);
    }
  });

  /* ---------- cart ---------- */
  async function fetchCart() {
    const res = await fetch(`${settings.routes.cart}.js`);
    return res.json();
  }

  async function updateCartCount() {
    try {
      const cart = await fetchCart();
      qsa('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      return cart;
    } catch (err) {
      console.error(err);
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function animateCartIcon() {
    qsa('.header-cart').forEach((el) => {
      el.classList.remove('is-cart-pulse');
      void el.offsetWidth;
      el.classList.add('is-cart-pulse');
    });
    qsa('[data-cart-count]').forEach((el) => {
      el.classList.remove('is-count-pop');
      void el.offsetWidth;
      el.classList.add('is-count-pop');
    });
  }

  function ensureAtcFeedback(btn) {
    if (!btn || btn.dataset.atcReady === '1') return;
    btn.classList.add('atc-btn');
    btn.dataset.atcReady = '1';

    if (!btn.querySelector('[data-atc-label-wrap]')) {
      const wrap = document.createElement('span');
      wrap.className = 'atc-btn__label';
      wrap.setAttribute('data-atc-label-wrap', '');
      while (btn.firstChild) wrap.appendChild(btn.firstChild);
      btn.appendChild(wrap);
    }

    if (!btn.querySelector('[data-atc-spinner]')) {
      const spinner = document.createElement('span');
      spinner.className = 'atc-btn__spinner';
      spinner.setAttribute('data-atc-spinner', '');
      spinner.setAttribute('aria-hidden', 'true');
      btn.appendChild(spinner);
    }

    if (!btn.querySelector('[data-atc-check]')) {
      const check = document.createElement('span');
      check.className = 'atc-btn__check';
      check.setAttribute('data-atc-check', '');
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';
      btn.appendChild(check);
    }
  }

  function syncAtcButton(atc, { available, label, price } = {}) {
    if (!atc) return;
    if (typeof available === 'boolean') atc.disabled = !available;
    if (label != null) {
      const textLabel = qs('[data-qv-atc-label]', atc) || qs('[data-sticky-atc-label]', atc);
      if (textLabel) {
        textLabel.textContent = label;
      } else {
        const wrap = qs('[data-atc-label-wrap]', atc);
        if (wrap && !qs('[data-qv-atc-price]', wrap)) wrap.textContent = label;
        else if (!wrap) atc.textContent = label;
      }
    }
    if (price != null) {
      const priceEl = qs('[data-qv-atc-price]', atc);
      if (priceEl) priceEl.textContent = price;
    }
  }

  function syncPurchaseLinks(scope, { available, variantId } = {}) {
    if (!scope) return;
    if (typeof available === 'boolean') {
      qsa('[data-combo-cta]', scope).forEach((el) => {
        el.hidden = !available;
      });
    }
    if (!variantId) return;
    qsa('[data-qv-product-link]', scope).forEach((link) => {
      try {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        url.searchParams.set('variant', String(variantId));
        link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      } catch (_) {}
    });
  }

  function setAtcState(btn, state) {
    if (!btn) return;
    ensureAtcFeedback(btn);
    btn.classList.remove('is-loading', 'is-added');
    if (state === 'loading') {
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      return;
    }
    if (state === 'added') {
      btn.classList.add('is-added');
      btn.removeAttribute('aria-busy');
      return;
    }
    btn.removeAttribute('aria-busy');
  }

  function showCartToast(message, options = {}) {
    if (!message) return;
    if (isCheckoutUpsellOpen() && !options.notice) {
      showCheckoutUpsellNotice(message);
      return;
    }
    let toast = qs('#CartAddedToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'CartAddedToast';
      toast.className = 'cart-added-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.toggle('is-notice', Boolean(options.notice));
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    window.clearTimeout(toast._hideTimer);
    toast._hideTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, options.duration || (options.notice ? 5600 : 1800));
  }

  function showAddedToast() {
    if (isCheckoutUpsellOpen()) {
      showCheckoutUpsellNotice(window.theme?.strings?.upsellAdded || 'Added to cart');
      return;
    }
    showCartToast(window.theme?.strings?.itemAdded || 'Added');
  }

  async function playAddSuccess(btn) {
    setAtcState(btn, 'added');
    showAddedToast();
    animateCartIcon();
    if (prefersReducedMotion()) return;
    await new Promise((resolve) => setTimeout(resolve, 450));
  }

  async function refreshCartDrawer(options = {}) {
    const drawer = qs('#CartDrawer');
    if (!drawer) return;
    const res = await fetch(`${settings.routes.cart}?section_id=cart-drawer`);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('#CartDrawer');
    const freshSheet = doc.querySelector('#DeliverySheet');
    const sheet = qs('#DeliverySheet');
    if (fresh) {
      const wasOpen = drawer.classList.contains('is-open');
      drawer.innerHTML = fresh.innerHTML;
      if (fresh.dataset.deliveryRequired) {
        drawer.dataset.deliveryRequired = fresh.dataset.deliveryRequired;
      }
      if (wasOpen) {
        drawer.hidden = false;
        drawer.classList.add('is-open');
      }
    }
    if (freshSheet && sheet) {
      const sheetWasOpen = sheet.classList.contains('is-open');
      const resumeCheckout = sheet.dataset.resumeCheckout || '';
      const deliveryConfirmed = sheet.dataset.deliveryConfirmed || '';
      const resumeCart = sheet.dataset.resumeCart || '';
      const resumeQuickView = sheet.dataset.resumeQuickView || '';
      sheet.innerHTML = freshSheet.innerHTML;
      if (resumeCheckout) sheet.dataset.resumeCheckout = resumeCheckout;
      if (deliveryConfirmed) sheet.dataset.deliveryConfirmed = deliveryConfirmed;
      if (resumeCart) sheet.dataset.resumeCart = resumeCart;
      if (resumeQuickView) sheet.dataset.resumeQuickView = resumeQuickView;
      if (sheetWasOpen) {
        sheet.hidden = false;
        sheet.classList.add('is-open');
        sheet.setAttribute('aria-hidden', 'false');
      }
    }
    initDeliveryPickers(document);
    updateCountdowns();
    restoreDeliveryMovedNotice();

    // Keep the UI snappy: secondary cart sync can finish in the background.
    if (options.deferSecondary) {
      ensureDefaultDelivery().catch(() => {});
      updateCartCount().catch(() => {});
      syncGiftAddonQuantities().catch(() => {});
      syncOverlayState();
      return;
    }

    await ensureDefaultDelivery();
    await updateCartCount();
    await syncGiftAddonQuantities();
    syncOverlayState();
  }

  async function addToCart(form, options = {}) {
    const openDrawer = options.openDrawer !== false;
    const formData = new FormData(form);
    const res = await fetch(settings.routes.cartAdd, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }

    // Immediate success feedback — don't hold the drawer on the success animation.
    if (options.button) {
      setAtcState(options.button, 'added');
      showAddedToast();
      animateCartIcon();
    } else {
      showAddedToast();
      animateCartIcon();
    }

    if (isCheckoutUpsellOpen()) {
      const cart = await fetchCart();
      updateCheckoutUpsellSummary(cart);
      await syncGiftAddonQuantities(cart);
      return;
    }

    if (!openDrawer) {
      await refreshCartDrawer({ deferSecondary: true });
      return;
    }

    if (settings.cartType === 'drawer') {
      const quickView = qs('#QuickViewModal');
      if (quickView?.classList.contains('is-open')) {
        closeOverlay(quickView);
      }
      // Refresh drawer HTML, then open — skip blocking secondary cart calls.
      await refreshCartDrawer({ deferSecondary: true });
      openOverlay(qs('#CartDrawer'));
      window.setTimeout(animateCartIcon, 120);
      return;
    }

    const quickView = qs('#QuickViewModal');
    if (quickView?.classList.contains('is-open')) {
      closeOverlay(quickView);
    }
    window.location.href = settings.routes.cart;
  }

  async function addItemsToCart(items, button, successMessage, options = {}) {
    const res = await fetch(settings.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add items to cart');
    }

    setAtcState(button, 'added');
    if (!options.quiet) {
      showCartToast(successMessage || window.theme?.strings?.itemAdded || 'Added');
    }
    animateCartIcon();

    if (options.skipDrawer) {
      await updateCartCount();
      if (settings.cartType === 'drawer') {
        await refreshCartDrawer({ deferSecondary: true }).catch(() => {});
      }
      return;
    }

    if (settings.cartType === 'drawer') {
      await refreshCartDrawer({ deferSecondary: true });
      openOverlay(qs('#CartDrawer'));
      window.setTimeout(animateCartIcon, 120);
      return;
    }
    window.location.href = settings.routes.cart;
  }

  function getVariantCartQty(cart, variantId) {
    const id = String(variantId || '');
    return (cart.items || []).reduce((sum, item) => {
      if (String(item.variant_id) === id || String(item.id) === id) return sum + Number(item.quantity || 0);
      return sum;
    }, 0);
  }

  function getVariantLineKey(cart, variantId) {
    const id = String(variantId || '');
    const item = (cart.items || []).find((line) => String(line.variant_id) === id || String(line.id) === id);
    return item ? item.key : null;
  }

  function addonQtyControlsHtml(variantId, qty) {
    const decrease = window.theme?.strings?.decrease || 'Decrease quantity';
    const increase = window.theme?.strings?.increase || 'Increase quantity';
    return `
      <div class="gift-addon__qty" data-addon-qty data-variant-id="${variantId}">
        <button type="button" class="gift-addon__qty-btn" data-addon-qty-adjust="-1" aria-label="${decrease}">−</button>
        <span class="gift-addon__qty-value" data-addon-qty-value>${qty}</span>
        <button type="button" class="gift-addon__qty-btn" data-addon-qty-adjust="1" aria-label="${increase}">+</button>
      </div>
    `;
  }

  function addonAddButtonHtml(variantId, available) {
    const addLabel = window.theme?.strings?.addAddon || 'Add';
    const soldOut = window.theme?.strings?.soldOut || 'Sold out';
    const label = available ? addLabel : soldOut;
    const disabled = available ? '' : 'disabled';
    return `
      <form method="post" action="${settings.routes.cartAdd}" data-product-form class="gift-addon__form">
        <input type="hidden" name="id" value="${variantId}">
        <input type="hidden" name="quantity" value="1">
        <button type="submit" class="button button--secondary gift-addon__btn" data-addon-add ${disabled}>
          <span class="gift-addon__btn-label" data-addon-label>${label}</span>
          <span class="gift-addon__btn-spinner" aria-hidden="true"></span>
          <span class="gift-addon__btn-check" aria-hidden="true">✓</span>
        </button>
      </form>
    `;
  }

  function renderGiftAddonActions(actionsEl, qty) {
    if (!actionsEl) return;
    const variantId = actionsEl.dataset.variantId;
    const available = actionsEl.dataset.available !== 'false';
    const card = actionsEl.closest('[data-gift-addon]');
    if (qty > 0) {
      actionsEl.innerHTML = addonQtyControlsHtml(variantId, qty);
      if (card) card.classList.add('is-in-cart');
    } else {
      actionsEl.innerHTML = addonAddButtonHtml(variantId, available);
      if (card) card.classList.remove('is-in-cart');
    }
  }

  async function syncGiftAddonQuantities(cart) {
    const current = cart || (await fetchCart());
    qsa('[data-gift-addon-actions]').forEach((actionsEl) => {
      const qty = getVariantCartQty(current, actionsEl.dataset.variantId);
      const valueEl = qs('[data-addon-qty-value]', actionsEl);
      if (qty > 0 && valueEl) {
        valueEl.textContent = String(qty);
        actionsEl.closest('[data-gift-addon]')?.classList.add('is-in-cart');
      } else {
        renderGiftAddonActions(actionsEl, qty);
      }
    });
    return current;
  }

  async function changeAddonVariantQty(variantId, nextQty) {
    const cart = await fetchCart();
    const lineKey = getVariantLineKey(cart, variantId);
    const qty = Math.max(0, Number(nextQty) || 0);

    if (!lineKey) {
      if (qty <= 0) return fetchCart();
      const res = await fetch(settings.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: qty }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not update quantity');
      }
    } else {
      const res = await fetch(settings.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: qty }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not update quantity');
      }
    }

    await refreshCartDrawer();
    await updateCartCount();
    if (isCheckoutUpsellOpen()) {
      const nextCart = await fetchCart();
      updateCheckoutUpsellSummary(nextCart);
      if (qty > 0) {
        showCheckoutUpsellNotice(window.theme?.strings?.upsellAdded || 'Added to cart');
      }
    }
    return fetchCart();
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form], [data-quick-view-form]');
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('[type="submit"]');
    const addonBtn = form.querySelector('[data-addon-add]');
    const addonLabel = addonBtn && qs('[data-addon-label]', addonBtn);
    const defaultAddonLabel = window.theme?.strings?.addAddon || 'Add';
    const actionsEl = form.closest('[data-gift-addon-actions]');

    if (addonBtn) {
      addonBtn.classList.remove('is-added');
      addonBtn.classList.add('is-loading');
      addonBtn.disabled = true;
      addonBtn.setAttribute('aria-busy', 'true');
    } else if (btn) {
      btn.disabled = true;
      setAtcState(btn, 'loading');
    }

    try {
      await addToCart(form, { openDrawer: !addonBtn, button: addonBtn ? null : btn });
      if (addonBtn && actionsEl) {
        const cart = await fetchCart();
        const qty = getVariantCartQty(cart, actionsEl.dataset.variantId) || 1;
        renderGiftAddonActions(actionsEl, qty);
        await syncGiftAddonQuantities(cart);
      } else if (addonBtn) {
        addonBtn.classList.remove('is-loading');
        addonBtn.disabled = false;
        addonBtn.removeAttribute('aria-busy');
        if (addonLabel) addonLabel.textContent = defaultAddonLabel;
      }
    } catch (err) {
      alert(err.message);
      if (addonBtn) {
        addonBtn.classList.remove('is-loading', 'is-added');
        addonBtn.disabled = false;
        addonBtn.removeAttribute('aria-busy');
        if (addonLabel) {
          qsa('[data-addon-label]', addonBtn).forEach((el) => {
            el.textContent = defaultAddonLabel;
          });
        }
      } else if (btn) {
        setAtcState(btn, 'idle');
      }
    } finally {
      if (!addonBtn && btn) {
        btn.disabled = false;
        window.setTimeout(() => setAtcState(btn, 'idle'), prefersReducedMotion() ? 0 : 700);
      }
    }
  });

  document.addEventListener('click', async (e) => {
    const adjust = e.target.closest('[data-addon-qty-adjust]');
    if (!adjust) return;
    e.preventDefault();
    const wrap = adjust.closest('[data-addon-qty]');
    const actionsEl = adjust.closest('[data-gift-addon-actions]');
    if (!wrap || !actionsEl) return;

    const variantId = wrap.dataset.variantId || actionsEl.dataset.variantId;
    const valueEl = qs('[data-addon-qty-value]', wrap);
    const currentQty = Number(valueEl?.textContent || 0);
    const nextQty = Math.max(0, currentQty + Number(adjust.dataset.addonQtyAdjust || 0));

    wrap.classList.add('is-loading');
    try {
      const cart = await changeAddonVariantQty(variantId, nextQty);
      await syncGiftAddonQuantities(cart);
    } catch (err) {
      alert(err.message);
    } finally {
      wrap.classList.remove('is-loading');
    }
  });

  async function updateLineProperties(lineKey, properties) {
    const cart = await fetchCart();
    const item = (cart.items || []).find((line) => line.key === lineKey);
    if (!item) return;
    const nextProps = { ...(item.properties || {}), ...properties };
    Object.keys(nextProps).forEach((key) => {
      if (nextProps[key] == null || nextProps[key] === '') delete nextProps[key];
    });
    delete nextProps['Delivery date'];
    delete nextProps['Delivery time'];
    await fetch(settings.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: item.quantity, properties: nextProps }),
    });
  }

  function cartUpdateJsUrl() {
    const base = settings.routes.cartUpdate || `${settings.routes.cart}/update`;
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  async function updateCartAttributes(partial = {}) {
    const cart = await fetchCart();
    const next = { ...(cart.attributes || {}), ...partial };
    Object.keys(next).forEach((key) => {
      if (next[key] == null) next[key] = '';
    });
    const res = await fetch(cartUpdateJsUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: next }),
    });
    if (!res.ok) throw new Error('Could not update cart');
    const updated = await res.json();
    syncDeliveryEntry(updated);
    return updated;
  }

  async function saveCartDelivery(date, time, extraAttrs = {}) {
    return updateCartAttributes({
      'Delivery date': date ? formatCartDate(date) : '',
      'Delivery time': persistableTime(time),
      ...extraAttrs,
    });
  }

  document.addEventListener('change', async (e) => {
    const qty = e.target.closest('[data-cart-qty]');
    if (qty) {
      const key = qty.dataset.lineKey;
      const quantity = Number(qty.value);
      await fetch(settings.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      await refreshCartDrawer();
      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
      }
      return;
    }

    const lineProp = e.target.closest('[data-cart-line-prop]');
    if (lineProp) {
      const key = lineProp.dataset.lineKey;
      const propName = lineProp.dataset.cartLineProp;
      if (!key || !propName) return;
      try {
        await updateLineProperties(key, { [propName]: lineProp.value.trim() });
      } catch (err) {
        console.error(err);
      }
      return;
    }

    const cartAttr = e.target.closest('[data-cart-attr]');
    if (cartAttr) {
      const attrName = cartAttr.dataset.cartAttr;
      if (!attrName) return;
      try {
        await updateCartAttributes({ [attrName]: cartAttr.value.trim() });
      } catch (err) {
        console.error(err);
      }
    }
  });

  function clearTipPosition(wrap) {
    const bubble = qs('.field-tip__bubble, .cart-sender__tooltip', wrap);
    if (!bubble) return;
    bubble.style.left = '';
    bubble.style.top = '';
    bubble.style.right = '';
    bubble.style.bottom = '';
    bubble.style.transform = '';
    bubble.style.position = '';
    bubble.style.width = '';
    bubble.style.maxWidth = '';
  }

  function placeTipInViewport(wrap) {
    const bubble = qs('.field-tip__bubble, .cart-sender__tooltip', wrap);
    const btn = qs('[data-field-tip], [data-sender-tip]', wrap);
    if (!bubble || !btn) return;

    // Ensure measurable before reading size
    bubble.style.display = 'block';
    bubble.style.visibility = 'hidden';
    bubble.style.opacity = '0';
    bubble.style.position = 'fixed';
    bubble.style.left = '0';
    bubble.style.top = '0';
    bubble.style.right = 'auto';
    bubble.style.bottom = 'auto';
    bubble.style.transform = 'none';
    bubble.style.width = '';
    bubble.style.maxWidth = `calc(100vw - 1.25rem)`;

    const pad = 10;
    const btnRect = btn.getBoundingClientRect();
    const bw = Math.min(bubble.offsetWidth || 260, window.innerWidth - pad * 2);
    bubble.style.width = `${bw}px`;
    const bh = bubble.offsetHeight || 80;

    let left = btnRect.left + btnRect.width / 2 - bw / 2;
    left = Math.max(pad, Math.min(left, window.innerWidth - bw - pad));

    let top = btnRect.top - bh - 10;
    if (top < pad) {
      top = btnRect.bottom + 10;
    }
    if (top + bh > window.innerHeight - pad) {
      top = Math.max(pad, window.innerHeight - bh - pad);
    }

    bubble.style.left = `${Math.round(left)}px`;
    bubble.style.top = `${Math.round(top)}px`;
    bubble.style.visibility = '';
    bubble.style.opacity = '';
  }

  document.addEventListener('click', (e) => {
    const tipBtn = e.target.closest('[data-sender-tip], [data-field-tip]');
    qsa('.cart-sender__phone-wrap.is-tip-open, [data-field-tip-wrap].is-tip-open').forEach((wrap) => {
      if (!tipBtn || !wrap.contains(tipBtn)) {
        wrap.classList.remove('is-tip-open');
        clearTipPosition(wrap);
      }
    });
    if (!tipBtn) return;
    e.preventDefault();
    const wrap = tipBtn.closest('.cart-sender__phone-wrap, [data-field-tip-wrap]');
    if (!wrap) return;
    const willOpen = !wrap.classList.contains('is-tip-open');
    wrap.classList.toggle('is-tip-open');
    if (willOpen) {
      requestAnimationFrame(() => placeTipInViewport(wrap));
    } else {
      clearTipPosition(wrap);
    }
  });

  window.addEventListener(
    'resize',
    () => {
      qsa('.cart-sender__phone-wrap.is-tip-open, [data-field-tip-wrap].is-tip-open').forEach(placeTipInViewport);
    },
    { passive: true }
  );

  document.addEventListener(
    'scroll',
    () => {
      qsa('.cart-sender__phone-wrap.is-tip-open, [data-field-tip-wrap].is-tip-open').forEach(placeTipInViewport);
    },
    { passive: true, capture: true }
  );

  document.addEventListener('click', async (e) => {
    const confirmBtn = e.target.closest('[data-delivery-confirm]');
    if (!confirmBtn) return;
    e.preventDefault();
    const sheet = confirmBtn.closest('[data-delivery-sheet]') || qs('#DeliverySheet');
    const picker = sheet && qs('[data-cart-delivery]', sheet);
    if (!picker) {
      if (sheet) closeOverlay(sheet);
      return;
    }
    const dateInput = qs('[data-delivery-input]', picker);
    const timeInput = qs('[data-delivery-time-input]', picker);
    if (!dateInput?.value || !timeInput?.value) {
      const msg =
        qs('#CartDrawer')?.dataset.deliveryRequired ||
        'Please choose a delivery date and time before checkout';
      alert(msg);
      return;
    }
    confirmBtn.disabled = true;
    confirmBtn.classList.add('is-loading');
    confirmBtn.setAttribute('aria-busy', 'true');
    try {
      const cart = picker._persistCartDelivery ? await picker._persistCartDelivery() : null;
      const resumeUrl = sheet?.dataset?.resumeCheckout || '';
      if (sheet) {
        sheet.dataset.deliveryConfirmed = '1';
        sheet.dataset.resumeCheckout = '';
        if (resumeUrl) {
          sheet.dataset.resumeCart = '';
          sheet.dataset.resumeQuickView = '';
        }
        closeOverlay(sheet);
      }
      // Refresh cart drawer in background so entry chips update without blocking close.
      refreshCartDrawer({ deferSecondary: true }).catch(() => {});
      if (resumeUrl) {
        try {
          const latest = cart || (await fetchCart());
          if (shouldShowCheckoutUpsell(latest)) {
            await openCheckoutUpsell(resumeUrl);
          } else {
            window.location.href = resumeUrl;
          }
        } catch (err) {
          window.location.href = resumeUrl;
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      confirmBtn.classList.remove('is-loading');
      confirmBtn.removeAttribute('aria-busy');
      confirmBtn.disabled = false;
    }
  });

  document.addEventListener('click', (e) => {
    const adjust = e.target.closest('[data-qty-adjust]');
    if (!adjust) return;
    const wrap = adjust.closest('[data-quantity]');
    const input = wrap && qs('[data-cart-qty]', wrap);
    if (!input) return;
    e.preventDefault();
    const next = Math.max(0, Number(input.value || 0) + Number(adjust.dataset.qtyAdjust || 0));
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    await fetch(settings.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: remove.dataset.lineKey, quantity: 0 }),
    });
    await refreshCartDrawer();
    if (document.body.classList.contains('template-cart')) {
      window.location.reload();
    }
  });

  function getExistingDiscountCodes(root = document) {
    const field = qs('[data-cart-discount-codes]', root);
    if (!field || !field.value.trim()) return [];
    return field.value
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
  }

  function setDiscountError(scope, message) {
    const root = scope?.closest?.('[data-cart-discount]') || scope || document;
    const errorEl = qs('[data-cart-discount-error]', root);
    if (!errorEl) return;
    if (!message) {
      errorEl.hidden = true;
      errorEl.textContent = '';
      return;
    }
    errorEl.hidden = false;
    errorEl.textContent = message;
  }

  async function updateCartDiscount(codes) {
    const discount = Array.isArray(codes) ? codes.filter(Boolean).join(',') : String(codes || '');
    const res = await fetch(cartUpdateJsUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || data.description || window.theme?.strings?.discountError || 'Could not apply discount');
    }
    return data;
  }

  function normalizeDiscountCode(value) {
    return String(value || '')
      .trim()
      .toUpperCase();
  }

  function findDiscountCodeEntry(cart, code) {
    const needle = normalizeDiscountCode(code);
    return (cart.discount_codes || []).find((entry) => normalizeDiscountCode(entry.code) === needle);
  }

  function discountWasApplied(cart, code) {
    const needle = normalizeDiscountCode(code);
    if (!needle) return false;

    const match = findDiscountCodeEntry(cart, needle);
    if (match) return match.applicable !== false;

    const apps = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.discount_applications || []),
    ];
    if (apps.some((app) => normalizeDiscountCode(app.title || app.code) === needle)) return true;

    return (cart.items || []).some((item) => {
      const allocations = item.line_level_discount_allocations || item.discounts || [];
      return allocations.some((entry) => {
        const title = entry.title || entry.code || entry.discount_application?.title;
        return normalizeDiscountCode(title) === needle;
      });
    });
  }

  function getActiveDiscountTitles(cart) {
    const titles = new Set();
    (cart.cart_level_discount_applications || []).forEach((app) => {
      if (app.title) titles.add(app.title);
    });
    (cart.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => {
        if (discount.title) titles.add(discount.title);
      });
    });
    return Array.from(titles);
  }

  function discountConflictMessage(discountTitle) {
    const template =
      window.theme?.strings?.discountConflict ||
      'This code can’t be combined with “[[discount]]”. In Admin → Discounts, enable combinations on both offers.';
    return template.replace('[[discount]]', discountTitle || 'another discount');
  }

  async function applyDiscountCodes(codes, codeToVerify) {
    await updateCartDiscount(codes);
    const cart = await fetchCart();
    if (codeToVerify && !discountWasApplied(cart, codeToVerify)) {
      const entry = findDiscountCodeEntry(cart, codeToVerify);
      if (entry && entry.applicable === false) {
        const active = getActiveDiscountTitles(cart);
        if (active.length) {
          throw new Error(discountConflictMessage(active[0]));
        }
        throw new Error(
          window.theme?.strings?.discountNotApplicable ||
            window.theme?.strings?.discountNotEligible ||
            'This code isn’t valid for the items in your cart'
        );
      }
      throw new Error(window.theme?.strings?.discountError || 'That code couldn’t be applied');
    }
    return cart;
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-cart-discount-form]');
    if (!form) return;
    e.preventDefault();

    const wrap = form.closest('[data-cart-discount]') || form;
    const input = qs('[data-cart-discount-input]', form);
    const btn = qs('[data-cart-discount-apply]', form);
    const code = (input?.value || '').trim();
    setDiscountError(wrap, '');

    if (!code) {
      setDiscountError(wrap, window.theme?.strings?.discountInvalid || 'Enter a valid discount code');
      input?.focus();
      return;
    }

    const existing = getExistingDiscountCodes(wrap);
    const already = existing.some((c) => normalizeDiscountCode(c) === normalizeDiscountCode(code));
    const nextCodes = already ? existing : [...existing, code];

    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
    }

    try {
      await applyDiscountCodes(nextCodes, code);
      if (input) input.value = '';
      await refreshCartDrawer();
      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
      }
    } catch (err) {
      setDiscountError(wrap, err.message || window.theme?.strings?.discountError || 'That code couldn’t be applied');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
    }
  });

  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-discount-remove]');
    if (!remove) return;
    e.preventDefault();

    const wrap = remove.closest('[data-cart-discount]') || document;
    const code = remove.dataset.discountCode || '';
    const remaining = getExistingDiscountCodes(wrap).filter(
      (c) => normalizeDiscountCode(c) !== normalizeDiscountCode(code)
    );

    const controls = qsa('[data-cart-discount-remove]', wrap);
    controls.forEach((el) => {
      el.disabled = true;
    });
    setDiscountError(wrap, '');

    try {
      await updateCartDiscount(remaining);
      await refreshCartDrawer();
      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
      }
    } catch (err) {
      setDiscountError(wrap, err.message || window.theme?.strings?.discountError || 'That code couldn’t be applied');
      controls.forEach((el) => {
        el.disabled = false;
      });
    }
  });

  function setCheckoutLoading(btn, loading) {
    if (!btn) return;
    btn.classList.toggle('is-loading', loading);
    if (loading) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('aria-disabled');
    }
  }

  async function proceedToCheckout(checkoutUrl = '/checkout') {
    await ensureDefaultDelivery();
    const cart = await fetchCart();
    const date = cart.attributes?.['Delivery date'];
    const time = cart.attributes?.['Delivery time'];
    if (!date || !time) {
      const sheet = qs('#DeliverySheet');
      if (sheet) {
        sheet.dataset.resumeCheckout = checkoutUrl;
        openOverlay(sheet);
      } else {
        const msg =
          qs('#CartDrawer')?.dataset.deliveryRequired ||
          'Please choose a delivery date and time before checkout';
        alert(msg);
      }
      return;
    }

    if (shouldShowCheckoutUpsell(cart)) {
      await openCheckoutUpsell(checkoutUrl);
      return;
    }

    window.location.href = checkoutUrl;
  }

  function clearTransientLoadingStates() {
    qsa(
      '[data-cart-checkout].is-loading, [data-checkout-upsell-continue].is-loading, [data-delivery-confirm].is-loading'
    ).forEach((btn) => {
      setCheckoutLoading(btn, false);
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('aria-disabled');
      if ('disabled' in btn) btn.disabled = false;
    });
  }

  // Browser back from checkout restores a cached page with the spinner still on.
  window.addEventListener('pageshow', (e) => {
    clearTransientLoadingStates();
    if (e.persisted) clearTransientLoadingStates();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') clearTransientLoadingStates();
  });

  document.addEventListener('click', async (e) => {
    const checkout = e.target.closest('[data-cart-checkout]');
    if (!checkout) return;
    e.preventDefault();
    if (checkout.classList.contains('is-loading')) return;
    const checkoutUrl = checkout.getAttribute('href') || '/checkout';
    setCheckoutLoading(checkout, true);
    try {
      await proceedToCheckout(checkoutUrl);
    } catch (err) {
      window.location.href = checkoutUrl;
    } finally {
      setCheckoutLoading(checkout, false);
    }
  });

  function closeCartDrawerIfOpen() {
    const drawer = qs('#CartDrawer');
    if (drawer?.classList.contains('is-open')) closeOverlay(drawer);
  }

  function getCheckoutUpsellProductIds() {
    const ids = window.theme?.settings?.checkoutUpsellProductIds;
    if (!Array.isArray(ids) || !ids.length) return null;
    return new Set(ids.map((id) => Number(id)));
  }

  function cartHasCheckoutUpsellProduct(cart, idSet) {
    if (!cart?.items?.length || !idSet?.size) return false;
    return cart.items.some((item) => idSet.has(Number(item.product_id)));
  }

  function wasCheckoutUpsellDismissed() {
    try {
      return sessionStorage.getItem('checkout-upsell-seen') === '1';
    } catch (err) {
      return false;
    }
  }

  function markCheckoutUpsellDismissed() {
    try {
      sessionStorage.setItem('checkout-upsell-seen', '1');
    } catch (err) {
      /* ignore */
    }
  }

  function shouldShowCheckoutUpsell(cart) {
    if (window.theme?.settings?.checkoutUpsellEnabled === false) return false;
    const modal = qs('#CheckoutUpsell');
    if (!modal) return false;
    if (wasCheckoutUpsellDismissed()) return false;
    const idSet = getCheckoutUpsellProductIds();
    if (!idSet || !idSet.size) return false;
    return !cartHasCheckoutUpsellProduct(cart, idSet);
  }

  async function openCheckoutUpsell(checkoutUrl) {
    const modal = qs('#CheckoutUpsell');
    if (!modal) {
      window.location.href = checkoutUrl || '/checkout';
      return;
    }
    modal.dataset.checkoutUrl = checkoutUrl || '/checkout';
    closeCartDrawerIfOpen();
    openOverlay(modal);
    try {
      const cart = await fetchCart();
      updateCheckoutUpsellSummary(cart);
      await syncGiftAddonQuantities(cart);
    } catch (err) {
      /* ignore sync errors; modal still usable */
    }
  }

  function isCheckoutUpsellOpen() {
    return Boolean(qs('#CheckoutUpsell.is-open'));
  }

  function updateCheckoutUpsellSummary(cart) {
    const modal = qs('#CheckoutUpsell');
    if (!modal || !cart) return;

    const countEl = qs('[data-checkout-upsell-count]', modal);
    const totalEl = qs('[data-checkout-upsell-total]', modal);
    const count = Number(cart.item_count) || 0;
    const total = Number(cart.total_price) || 0;

    if (countEl) {
      const one = window.theme?.strings?.upsellItemOne || '1 item';
      const otherTpl = window.theme?.strings?.upsellItemOther || '[[count]] items';
      countEl.textContent =
        count === 1 ? one.replace('{{ count }}', '1') : otherTpl.replace('[[count]]', String(count)).replace('{{ count }}', String(count));
    }
    if (totalEl) totalEl.textContent = formatMoney(total);

    const summary = qs('[data-checkout-upsell-summary]', modal);
    if (summary) summary.classList.add('is-updated');
    window.clearTimeout(updateCheckoutUpsellSummary._pulse);
    updateCheckoutUpsellSummary._pulse = window.setTimeout(() => {
      summary?.classList.remove('is-updated');
    }, 500);
  }

  function showCheckoutUpsellNotice(text) {
    const modal = qs('#CheckoutUpsell');
    const notice = qs('[data-checkout-upsell-notice]', modal || document);
    if (!notice) return;
    notice.hidden = false;
    notice.textContent = text || window.theme?.strings?.upsellAdded || 'Added to cart';
    notice.classList.remove('is-visible');
    void notice.offsetWidth;
    notice.classList.add('is-visible');
    window.clearTimeout(showCheckoutUpsellNotice._timer);
    showCheckoutUpsellNotice._timer = window.setTimeout(() => {
      notice.classList.remove('is-visible');
      window.setTimeout(() => {
        if (!notice.classList.contains('is-visible')) notice.hidden = true;
      }, 280);
    }, 2000);
  }

  function goToCheckoutFromUpsell(source) {
    const modal = qs('#CheckoutUpsell');
    const url = source?.dataset?.checkoutUrl || modal?.dataset?.checkoutUrl || '/checkout';
    markCheckoutUpsellDismissed();
    if (source) setCheckoutLoading(source, true);
    // Keep URL on the modal so closeOverlay resumes checkout once.
    if (modal) modal.dataset.checkoutUrl = url;
    if (modal) closeOverlay(modal);
    else window.location.href = url;
  }

  document.addEventListener('click', (e) => {
    const continueBtn = e.target.closest('[data-checkout-upsell-continue]');
    if (!continueBtn) return;
    e.preventDefault();
    goToCheckoutFromUpsell(continueBtn);
  });

  /* ---------- delivery datepicker + timeslots ---------- */
  const STORE_TZ = settings.timezone || 'Asia/Kuwait';
  const ASAP_VALUE = settings.asapValue || 'As Soon As Possible';
  const ASAP_LABEL = settings.asapLabel || 'As Soon As Possible';

  function parseCutoff(cutoff) {
    const [h, m] = String(cutoff || '23:00').split(':').map(Number);
    return { h: h || 14, m: m || 0 };
  }

  function getStoreNow() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: STORE_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const map = {};
    parts.forEach((p) => {
      if (p.type !== 'literal') map[p.type] = p.value;
    });
    const year = Number(map.year);
    const month = Number(map.month);
    const day = Number(map.day);
    let hour = Number(map.hour);
    if (hour === 24) hour = 0;
    const minute = Number(map.minute);
    return {
      year,
      month,
      day,
      hour,
      minute,
      minutes: hour * 60 + minute,
      iso: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    };
  }

  function toIsoDate(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const match = raw.match(/^([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4})$/);
    if (!match) return '';
    const months = {
      jan: 1,
      feb: 2,
      mar: 3,
      apr: 4,
      may: 5,
      jun: 6,
      jul: 7,
      aug: 8,
      sep: 9,
      oct: 10,
      nov: 11,
      dec: 12,
    };
    const month = months[match[1].toLowerCase()];
    if (!month) return '';
    return `${match[3]}-${String(month).padStart(2, '0')}-${String(Number(match[2])).padStart(2, '0')}`;
  }

  function storeDateFromISO(iso) {
    const normalized = toIsoDate(iso) || String(iso || '');
    const [y, m, d] = normalized.split('-').map(Number);
    return { year: y, month: m, day: d };
  }

  function formatCartDate(value) {
    const iso = toIsoDate(value);
    if (!iso) return value || '';
    const [year, month, day] = iso.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[Number(month) - 1]} ${day}, ${year}`;
  }

  function addStoreDays(iso, days) {
    const base = storeDateFromISO(iso);
    const utc = new Date(Date.UTC(base.year, base.month - 1, base.day + days));
    return `${utc.getUTCFullYear()}-${String(utc.getUTCMonth() + 1).padStart(2, '0')}-${String(utc.getUTCDate()).padStart(2, '0')}`;
  }

  function sameDayAvailable() {
    if (!settings.sameDayEnabled) return false;
    const now = getStoreNow();
    const { h, m } = parseCutoff(settings.sameDayCutoff);
    return now.minutes < h * 60 + m;
  }

  function formatCountdown(ms) {
    if (ms <= 0) return null;
    const total = Math.floor(ms / 1000);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    if (hours <= 0) return `${minutes}m`;
    if (minutes <= 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  }

  function countdownUnits(ms) {
    if (ms <= 0) return null;
    const total = Math.floor(ms / 1000);
    return {
      hours: Math.floor(total / 3600),
      minutes: Math.floor((total % 3600) / 60),
    };
  }

  function countdownPillHtml(el, ms) {
    const split = el.classList.contains('product__same-day-countdown');
    if (!split) {
      return `<span class="delivery-picker__countdown-pill">${formatCountdown(ms)}</span>`;
    }
    const units = countdownUnits(ms);
    if (!units) return '';
    const host = el.closest('[data-product-same-day]') || el;
    const hrsLabel = host.dataset.hrsLabel || 'hrs';
    const minLabel = host.dataset.minLabel || 'min';
    const urgent = units.hours < 1;
    const hourBlock =
      units.hours > 0
        ? `<span class="product__same-day-timer-unit"><strong>${units.hours}</strong><small>${hrsLabel}</small></span>`
        : '';
    const minuteValue = units.hours > 0 ? String(units.minutes).padStart(2, '0') : String(units.minutes);
    return `<span class="delivery-picker__countdown-pill product__same-day-timer${
      urgent ? ' is-urgent' : ''
    }" aria-label="${units.hours} ${hrsLabel} ${units.minutes} ${minLabel}"><span class="product__same-day-timer-units">${hourBlock}<span class="product__same-day-timer-unit"><strong>${minuteValue}</strong><small>${minLabel}</small></span></span></span>`;
  }

  function countdownTemplates(el) {
    const host = el?.closest('[data-delivery-picker], [data-product-same-day]') || el;
    return {
      open: host?.dataset?.orderWithin || 'Same-day still open · Order in {{ time }}',
      closed: host?.dataset?.sameDayClosed || 'Same-day has closed · Pick tomorrow or later',
    };
  }

  function splitCountdownCopy(template) {
    const parts = String(template || '')
      .split('·')
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      label: (parts[0] || '').replace('{{ time }}', '').trim(),
      meta: (parts[1] || '').replace('{{ time }}', '').replace(/\s+/g, ' ').trim(),
    };
  }

  function updateCountdowns() {
    const clockSvg =
      '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8"/><path d="M12 8v5l3 1.5"/></svg>';

    if (!settings.sameDayEnabled) {
      qsa('[data-delivery-countdown]').forEach((el) => {
        const { closed } = countdownTemplates(el);
        const { label, meta } = splitCountdownCopy(closed);
        el.classList.add('is-closed');
        el.innerHTML = `${clockSvg}<span class="delivery-picker__countdown-copy"><span class="delivery-picker__countdown-label">${label}</span>${
          meta ? `<span class="delivery-picker__countdown-meta">${meta}</span>` : ''
        }</span>`;
      });
      return;
    }
    const now = getStoreNow();
    const { h, m } = parseCutoff(settings.sameDayCutoff);
    const remainingMins = h * 60 + m - now.minutes;
    const remaining = remainingMins * 60 * 1000;
    const time = formatCountdown(remaining);
    qsa('[data-delivery-countdown]').forEach((el) => {
      const { open, closed } = countdownTemplates(el);
      if (time && remainingMins > 0) {
        const { label, meta } = splitCountdownCopy(open);
        el.classList.remove('is-closed');
        el.innerHTML = `${clockSvg}<span class="delivery-picker__countdown-copy"><span class="delivery-picker__countdown-label">${label}</span>${
          meta ? `<span class="delivery-picker__countdown-meta">${meta}</span>` : ''
        }</span>${countdownPillHtml(el, remaining)}`;
      } else {
        const { label, meta } = splitCountdownCopy(closed);
        el.classList.add('is-closed');
        el.innerHTML = `${clockSvg}<span class="delivery-picker__countdown-copy"><span class="delivery-picker__countdown-label">${label}</span>${
          meta ? `<span class="delivery-picker__countdown-meta">${meta}</span>` : ''
        }</span>`;
      }
    });
  }

  function isAsap(value) {
    return String(value || '').trim().toLowerCase() === ASAP_VALUE.toLowerCase();
  }

  function parseTimeslots(raw) {
    const source = raw || settings.deliveryTimeslots || '';
    return String(source)
      .split(/[\n|]+/)
      .map((s) => s.trim())
      .filter((s) => s && !isAsap(s));
  }

  function parseClockToMinutes(token) {
    const raw = String(token || '').trim();
    if (!raw) return null;
    const ampm = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (ampm) {
      let hour = Number(ampm[1]);
      const minute = Number(ampm[2] || 0);
      const period = ampm[3].toUpperCase();
      if (period === 'AM') {
        if (hour === 12) hour = 0;
      } else if (hour !== 12) {
        hour += 12;
      }
      return hour * 60 + minute;
    }
    const h24 = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (h24) {
      let hour = Number(h24[1]);
      if (hour === 24) hour = 0;
      return hour * 60 + Number(h24[2]);
    }
    return null;
  }

  function slotStartMinutes(label) {
    if (isAsap(label)) return -1;
    const left = String(label).split(/[–—-]/)[0];
    const mins = parseClockToMinutes(left);
    return mins == null ? 0 : mins;
  }

  function minDeliverableDate() {
    const today = getStoreNow().iso;
    if (sameDayAvailable()) return today;
    return addStoreDays(today, Math.max(1, settings.leadDays || 1));
  }

  function isSlotPassed(slot, selectedDate) {
    const iso = toIsoDate(selectedDate) || String(selectedDate || '');
    if (!iso || !slot) return true;
    const todayIso = getStoreNow().iso;
    if (iso < todayIso) return true;
    if (iso > todayIso) return false;
    if (isAsap(slot)) return !sameDayAvailable();
    const start = slotStartMinutes(slot);
    return start <= getStoreNow().minutes;
  }

  function earliestSlotsForDate(selectedDate) {
    const todayIso = getStoreNow().iso;
    const list = [];
    if (selectedDate === todayIso && sameDayAvailable()) list.push(ASAP_VALUE);
    parseTimeslots(settings.deliveryTimeslots).forEach((slot) => list.push(slot));
    return list;
  }

  function firstOpenSlot(selectedDate) {
    return earliestSlotsForDate(selectedDate).find((slot) => !isSlotPassed(slot, selectedDate)) || '';
  }

  function earliestAvailableDelivery() {
    let date = minDeliverableDate();
    let time = firstOpenSlot(date);
    if (!time) {
      date = addStoreDays(date, 1);
      time = firstOpenSlot(date) || parseTimeslots(settings.deliveryTimeslots)[0] || '06:00 – 09:00';
    }
    return { date, time };
  }

  function isDeliveryUnavailable(date, time) {
    if (!date || !time) return true;
    const iso = toIsoDate(date) || date;
    if (iso < minDeliverableDate()) return true;
    return isSlotPassed(time, iso);
  }

  let deliveryMovedNotice = false;
  let deliveryEnsurePromise = null;

  function setDeliveryMovedNotice(show) {
    deliveryMovedNotice = Boolean(show);
    try {
      if (show) sessionStorage.setItem('delivery-moved-notice', '1');
      else sessionStorage.removeItem('delivery-moved-notice');
    } catch (err) {
      /* ignore */
    }
    qsa('[data-delivery-moved-notice]').forEach((el) => {
      el.hidden = !deliveryMovedNotice;
    });
  }

  function restoreDeliveryMovedNotice() {
    if (deliveryMovedNotice) {
      setDeliveryMovedNotice(true);
      return;
    }
    try {
      if (sessionStorage.getItem('delivery-moved-notice') === '1') setDeliveryMovedNotice(true);
    } catch (err) {
      /* ignore */
    }
  }

  function notifyDeliveryMoved(next) {
    const summary = formatDeliverySummary(next.date, next.time);
    const toast =
      window.theme?.strings?.deliveryMovedToast ||
      'Your previous time was no longer available. We moved you to the next earliest window.';
    showCartToast(toast.replace('{{ summary }}', summary), { notice: true });
    setDeliveryMovedNotice(true);
  }

  function refreshCartDeliveryPickers(date, time) {
    const iso = toIsoDate(date) || date;
    qsa('[data-cart-delivery]').forEach((root) => {
      const dateInput = qs('[data-delivery-input]', root);
      const timeInput = qs('[data-delivery-time-input]', root);
      if (dateInput) dateInput.value = iso || '';
      if (timeInput) timeInput.value = time || '';
      root.dataset.committedDate = iso || '';
      root.dataset.committedTime = time || '';
      delete root.dataset.ready;
      buildCalendar(root);
    });
  }

  function formatHourLabel(totalMinutes) {
    let mins = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
    let hour = Math.floor(mins / 60);
    const minute = mins % 60;
    const period = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return minute === 0 ? `${hour} ${period}` : `${hour}:${String(minute).padStart(2, '0')} ${period}`;
  }

  function formatSlotLabel(slot) {
    if (isAsap(slot)) return ASAP_LABEL;
    const parts = String(slot).split(/[–—-]/).map((s) => s.trim());
    if (parts.length < 2) return slot;
    const start = parseClockToMinutes(parts[0]);
    const end = parseClockToMinutes(parts[1]);
    if (start == null || end == null) return slot;
    return `${formatHourLabel(start)} – ${formatHourLabel(end)}`;
  }

  function persistableTime(slot) {
    if (!slot) return '';
    if (isAsap(slot)) return ASAP_VALUE;
    return formatSlotLabel(slot);
  }

  function sameSlot(a, b) {
    if (!a || !b) return false;
    if (isAsap(a) && isAsap(b)) return true;
    if (String(a).trim() === String(b).trim()) return true;
    return slotStartMinutes(a) === slotStartMinutes(b) && formatSlotLabel(a) === formatSlotLabel(b);
  }

  function formatDisplayDate(iso) {
    const normalized = toIsoDate(iso);
    if (!normalized) return iso || '';
    const date = storeDateFromISO(normalized);
    const probe = new Date(Date.UTC(date.year, date.month - 1, date.day, 12));
    return new Intl.DateTimeFormat(undefined, {
      timeZone: 'UTC',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(probe);
  }

  function formatDeliveryTime(time) {
    if (!time || isAsap(time)) return ASAP_LABEL;
    return formatSlotLabel(time);
  }

  function formatDeliverySummary(date, time) {
    if (!time && !date) return ASAP_LABEL;
    const niceTime = formatDeliveryTime(time);
    if (!date) return niceTime;
    return `${formatDisplayDate(date)} · ${niceTime}`;
  }

  function fillDeliveryCardWhen(date, time) {
    const hasTime = Boolean(date && time);
    const dateText = hasTime ? formatDisplayDate(date) : '';
    const timeText = hasTime ? formatDeliveryTime(time) : '';
    qsa('[data-delivery-entry]').forEach((entry) => {
      const prompt = entry.dataset.choosePrompt || 'Choose time, notes & contact';
      const dateEl = qs('[data-delivery-date-text]', entry);
      const footer = qs('[data-delivery-footer-text]', entry);
      if (dateEl) {
        dateEl.textContent = dateText;
        dateEl.hidden = !hasTime;
      }
      if (footer) footer.textContent = hasTime ? timeText : prompt;
    });
  }

  function syncAsapHints(date, time) {
    const showHint = Boolean(date && time && isAsap(time));
    qsa('[data-delivery-entry]').forEach((entry) => {
      const hint = qs('[data-delivery-asap-hint]', entry);
      if (!hint) return;
      if (showHint && !hint.textContent.trim()) {
        hint.textContent = entry.dataset.asapHint || '';
      }
      hint.hidden = !showHint;
    });
  }

  function syncDeliveryEntry(cart) {
    const attrs = cart?.attributes || {};
    const date = attrs['Delivery date'] || '';
    const time = attrs['Delivery time'] || '';
    const hasTime = Boolean(date && time);
    const hasInstructions = Boolean((attrs['Delivery instructions'] || '').trim());
    const hasSender = Boolean((attrs['Sender name'] || '').trim() || (attrs['Sender phone'] || '').trim());
    const complete = hasTime;
    const summaryText = hasTime ? formatDeliverySummary(date, time) : '';
    syncProductDeliverySummary(date, time);
    syncAsapHints(date, time);
    fillDeliveryCardWhen(date, time);

    qsa('[data-delivery-entry]').forEach((entry) => {
      const setupLabel = entry.dataset.setupLabel || 'Choose time';
      const editLabel = entry.dataset.editLabel || 'Change';
      entry.classList.toggle('cart-delivery-entry--needs-attention', !complete);
      const timeChip = qs('[data-delivery-chip="time"]', entry);
      const instructionsChip = qs('[data-delivery-chip="instructions"]', entry);
      const senderChip = qs('[data-delivery-chip="sender"]', entry);
      if (timeChip) timeChip.classList.toggle('is-done', hasTime);
      if (instructionsChip) instructionsChip.classList.toggle('is-done', hasInstructions);
      if (senderChip) senderChip.classList.toggle('is-done', hasSender);
      const cta = qs('[data-delivery-entry-cta]', entry);
      if (cta) {
        cta.textContent = complete ? editLabel : setupLabel;
        cta.classList.toggle('is-primary', !complete);
      }
    });

    qsa('[data-delivery-sheet-summary]').forEach((el) => {
      el.textContent = summaryText;
    });
  }

  function syncDeliverySummaries(date, time) {
    const text = formatDeliverySummary(date, time);
    fillDeliveryCardWhen(date, time);
    syncProductDeliverySummary(date, time);
    qsa('[data-delivery-sheet-summary]').forEach((el) => {
      el.textContent = text;
    });
    qsa('[data-delivery-chip="time"]').forEach((chip) => {
      chip.classList.toggle('is-done', Boolean(date && time));
    });
    syncAsapHints(date, time);
  }

  async function ensureDefaultDelivery() {
    if (deliveryEnsurePromise) return deliveryEnsurePromise;
    deliveryEnsurePromise = (async () => {
      try {
        const cart = await fetchCart();
        if (!cart?.item_count) return cart;
        const date = cart.attributes?.['Delivery date'] || '';
        const time = cart.attributes?.['Delivery time'] || '';
        if (date && time && !isDeliveryUnavailable(date, time)) {
          const readable = persistableTime(time);
          if (readable && readable !== time) {
            const updated = await saveCartDelivery(date, readable);
            syncDeliverySummaries(date, readable);
            if (updated) syncDeliveryEntry(updated);
            refreshCartDeliveryPickers(date, readable);
            restoreDeliveryMovedNotice();
            return updated;
          }
          syncDeliveryEntry(cart);
          restoreDeliveryMovedNotice();
          return cart;
        }
        const next = earliestAvailableDelivery();
        const updated = await saveCartDelivery(next.date, next.time);
        syncDeliverySummaries(next.date, next.time);
        if (updated) syncDeliveryEntry(updated);
        refreshCartDeliveryPickers(next.date, next.time);
        if (date && time) notifyDeliveryMoved(next);
        else restoreDeliveryMovedNotice();
        return updated;
      } catch (err) {
        console.error(err);
        return null;
      }
    })().finally(() => {
      deliveryEnsurePromise = null;
    });
    return deliveryEnsurePromise;
  }

  /* ---------- collapse app embeds that render nothing ---------- */
  function initAppBlockCollapse() {
    if (window.Shopify && window.Shopify.designMode) return;
    const blocks = qsa('[data-app-collapse]');
    if (!blocks.length) return;

    function isFilled(el) {
      if (!el.querySelector('img, svg, iframe, canvas, video, input, button, a, [class]')) {
        if (!el.textContent.trim()) return false;
      }
      return el.getBoundingClientRect().height > 2;
    }

    function sync() {
      blocks.forEach((el) => {
        el.classList.toggle('is-empty', !isFilled(el));
      });
      qsa('[data-collapse-when-empty]').forEach((section) => {
        const hasApp = qsa('[data-app-collapse]', section).some(
          (el) => !el.classList.contains('is-empty')
        );
        section.classList.toggle('is-empty', !hasApp && !qs('.review-card', section));
      });
    }

    sync();

    // Apps inject asynchronously, so re-check on DOM changes and after load.
    const observer = new MutationObserver(() => sync());
    blocks.forEach((el) => observer.observe(el, { childList: true, subtree: true }));
    [800, 2500, 5000].forEach((delay) => window.setTimeout(sync, delay));
    window.addEventListener('load', sync, { once: true });
  }

  function syncProductDeliverySummary(date, time) {
    const hasDelivery = Boolean(date && time);
    const showAsapHint = hasDelivery && isAsap(time);
    qsa('[data-product-delivery-summary]').forEach((root) => {
      const value = qs('[data-product-delivery-value]', root);
      const action = qs('[data-product-delivery-action]', root);
      const hint = qs('[data-product-delivery-asap-hint]', root);
      const promise = qs('[data-product-delivery-promise]', root);
      root.classList.toggle('product-delivery-summary--empty', !hasDelivery);
      if (value) {
        value.textContent = hasDelivery
          ? formatDeliverySummary(date, time)
          : root.dataset.emptyLabel || '';
      }
      if (action) {
        action.textContent = hasDelivery
          ? root.dataset.changeLabel || 'Change'
          : root.dataset.chooseLabel || 'Choose';
      }
      if (hint) {
        if (showAsapHint && !hint.textContent.trim()) {
          hint.textContent = root.dataset.asapHint || '';
        }
        hint.hidden = !showAsapHint;
      }
      if (promise) {
        promise.hidden = showAsapHint;
      }
    });
  }

  function buildCalendar(root) {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    const dateInput = qs('[data-delivery-input]', root);
    const timeInput = qs('[data-delivery-time-input]', root);
    const slotsWrap = qs('[data-delivery-slots]', root);
    const daysWrap = qs('[data-delivery-days]', root);
    const calendarWrap = qs('[data-delivery-calendar]', root);
    const timeHint = qs('[data-delivery-time-hint]', root);
    const moreDateBtn = qs('[data-delivery-more-date]', root);
    const isCartDelivery = root.hasAttribute('data-cart-delivery');
    const dayCount = Number(root.dataset.dayCount || 10);
    const labelToday = root.dataset.labelToday || 'Today';
    const labelTomorrow = root.dataset.labelTomorrow || 'Tomorrow';
    const asapLabel = root.dataset.asapLabel || ASAP_LABEL;
    const asapValue = root.dataset.asapValue || ASAP_VALUE;
    const asapHint = root.dataset.asapHint || 'Typically arrives within 2 hours';
    const sameDayBadge = root.dataset.sameDayBadge || 'Same day';
    const groupMorningLabel = root.dataset.slotGroupMorning || 'Morning';
    const groupAfternoonLabel = root.dataset.slotGroupAfternoon || 'Afternoon';
    const groupEveningLabel = root.dataset.slotGroupEvening || 'Evening';

    if (!dateInput || !slotsWrap) return;
    if (dateInput.value) dateInput.value = toIsoDate(dateInput.value) || dateInput.value;

    let calendarView = null;

    function minDateIso() {
      const today = getStoreNow().iso;
      if (sameDayAvailable()) return today;
      return addStoreDays(today, Math.max(1, settings.leadDays || 1));
    }

    function maxDateIso() {
      return addStoreDays(getStoreNow().iso, settings.maxDeliveryDays || 45);
    }

    function dayLabel(iso) {
      const todayIso = getStoreNow().iso;
      const tomorrowIso = addStoreDays(todayIso, 1);
      if (iso === todayIso) return labelToday;
      if (iso === tomorrowIso) return labelTomorrow;
      const d = storeDateFromISO(iso);
      const probe = new Date(Date.UTC(d.year, d.month - 1, d.day, 12));
      return new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', weekday: 'short' }).format(probe);
    }

    function dayNumber(iso) {
      return storeDateFromISO(iso).day;
    }

    function dayMonth(iso) {
      const d = storeDateFromISO(iso);
      const probe = new Date(Date.UTC(d.year, d.month - 1, d.day, 12));
      return new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', month: 'short' }).format(probe);
    }

    function syncSummary() {
      const timeText = timeInput?.value || '';
      if (timeHint) timeHint.hidden = Boolean(timeText);
      const text = formatDeliverySummary(dateInput.value, timeText);
      // Cart sheet: only update the in-sheet preview until Save details is clicked.
      const sheet = root.closest('[data-delivery-sheet]');
      if (isCartDelivery && sheet) {
        qsa('[data-delivery-sheet-summary]', sheet).forEach((el) => {
          el.textContent = text;
        });
        return;
      }
      syncDeliverySummaries(dateInput.value, timeText);
    }

    async function persistCartDelivery() {
      if (!isCartDelivery) return null;
      try {
        const sheet = root.closest('[data-delivery-sheet]');
        const extra = {};
        if (sheet) {
          const instructions = qs('[data-cart-attr="Delivery instructions"]', sheet);
          const senderName = qs('[data-cart-attr="Sender name"]', sheet);
          const senderPhone = qs('[data-cart-attr="Sender phone"]', sheet);
          if (instructions) extra['Delivery instructions'] = instructions.value.trim();
          if (senderName) extra['Sender name'] = senderName.value.trim();
          if (senderPhone) extra['Sender phone'] = senderPhone.value.trim();
        }
        const cart = await saveCartDelivery(dateInput.value, timeInput?.value || '', extra);
        root.dataset.committedDate = dateInput.value || '';
        root.dataset.committedTime = timeInput?.value || '';
        setDeliveryMovedNotice(false);
        syncDeliverySummaries(dateInput.value, timeInput?.value || '');
        if (cart) syncDeliveryEntry(cart);
        return cart;
      } catch (err) {
        console.error(err);
        throw err;
      }
    }

    function revertToCommitted() {
      if (!isCartDelivery) return;
      const date = root.dataset.committedDate || '';
      const time = root.dataset.committedTime || '';
      dateInput.value = date;
      if (timeInput) timeInput.value = time;
      renderDays();
      renderSlots({ autoSlot: false });
      syncSummary();
      syncDeliverySummaries(date, time);
    }

    root._persistCartDelivery = persistCartDelivery;
    root._revertDeliveryDraft = revertToCommitted;
    root._captureCommittedDelivery = () => {
      root.dataset.committedDate = dateInput.value || '';
      root.dataset.committedTime = timeInput?.value || '';
    };

    function configuredSlots() {
      return parseTimeslots(root.dataset.timeslots);
    }

    function slotsForDate(selectedDate) {
      const todayIso = getStoreNow().iso;
      const list = [];
      if (selectedDate === todayIso && sameDayAvailable()) {
        list.push(asapValue);
      }
      configuredSlots().forEach((slot) => list.push(slot));
      return list;
    }

    function firstAvailableSlot(selectedDate) {
      return slotsForDate(selectedDate).find((slot) => !isSlotPassed(slot, selectedDate)) || '';
    }

    function revealExtras() {
      if (!isCartDelivery) return;
      const extras = qs('[data-delivery-extras]', root);
      if (!extras) return;
      extras.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      extras.classList.remove('is-nudged');
      // Restart the highlight animation on every slot change.
      void extras.offsetWidth;
      extras.classList.add('is-nudged');
      window.setTimeout(() => extras.classList.remove('is-nudged'), 1600);
    }

    function slotPeriod(slot) {
      if (isAsap(slot)) return 'asap';
      const start = slotStartMinutes(slot);
      if (start < 12 * 60) return 'morning';
      if (start < 17 * 60) return 'afternoon';
      return 'evening';
    }

    function createSlotButton(slot) {
      const btn = document.createElement('button');
      btn.type = 'button';
      const asap = isAsap(slot);
      btn.className = 'delivery-picker__slot' + (asap ? ' delivery-picker__slot--asap' : '');
      btn.dataset.deliverySlot = '';
      btn.dataset.slot = slot;
      btn.setAttribute('role', 'option');
      if (asap) {
        btn.innerHTML =
          '<span class="delivery-picker__slot-title">' +
          asapLabel +
          '</span><span class="delivery-picker__slot-hint">' +
          asapHint +
          '</span>';
      } else {
        btn.textContent = formatSlotLabel(slot);
      }
      btn.addEventListener('click', () => {
        qsa('.delivery-picker__slot', slotsWrap).forEach((el) => {
          el.classList.remove('is-selected');
          el.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-selected', 'true');
        if (timeInput) timeInput.value = persistableTime(slot);
        syncSummary();
        revealExtras();
      });
      return btn;
    }

    function appendSlotGroup(label, slots) {
      if (!slots.length) return;
      const group = document.createElement('div');
      group.className = 'delivery-picker__slot-group';
      const title = document.createElement('p');
      title.className = 'delivery-picker__slot-group-label';
      title.textContent = label;
      const grid = document.createElement('div');
      grid.className = 'delivery-picker__slot-group-grid';
      grid.setAttribute('role', 'presentation');
      slots.forEach((slot) => grid.appendChild(createSlotButton(slot)));
      group.appendChild(title);
      group.appendChild(grid);
      slotsWrap.appendChild(group);
    }

    function renderSlots({ autoSlot = false } = {}) {
      const selectedDate = dateInput.value;
      const slots = slotsForDate(selectedDate);
      let previous = timeInput?.value || '';

      slotsWrap.innerHTML = '';
      slotsWrap.classList.toggle('is-disabled', !selectedDate);
      slotsWrap.classList.remove('is-expanded');

      if (!selectedDate) {
        if (timeInput) timeInput.value = '';
        syncSummary();
        return;
      }

      const openSlots = slots.filter((slot) => !isSlotPassed(slot, selectedDate));
      const groups = {
        asap: [],
        morning: [],
        afternoon: [],
        evening: [],
      };
      openSlots.forEach((slot) => {
        groups[slotPeriod(slot)].push(slot);
      });

      if (groups.asap.length) {
        const asapWrap = document.createElement('div');
        asapWrap.className = 'delivery-picker__slot-group delivery-picker__slot-group--asap';
        groups.asap.forEach((slot) => asapWrap.appendChild(createSlotButton(slot)));
        slotsWrap.appendChild(asapWrap);
      }
      appendSlotGroup(groupMorningLabel, groups.morning);
      appendSlotGroup(groupAfternoonLabel, groups.afternoon);
      appendSlotGroup(groupEveningLabel, groups.evening);

      const stillValid = Boolean(previous) && openSlots.some((slot) => sameSlot(slot, previous));

      if (!stillValid) previous = autoSlot ? firstAvailableSlot(selectedDate) : '';
      if (timeInput) timeInput.value = previous ? persistableTime(previous) : '';
      if (previous) {
        qsa('.delivery-picker__slot', slotsWrap).forEach((el) => {
          const on = sameSlot(el.dataset.slot, previous);
          el.classList.toggle('is-selected', on);
          el.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }
      syncSummary();
    }

    function setDate(iso, { persist = !isCartDelivery, autoSlot = true } = {}) {
      dateInput.value = iso || '';
      if (daysWrap) {
        qsa('[data-delivery-day]', daysWrap).forEach((btn) => {
          const on = btn.dataset.date === iso;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }
      renderSlots({ autoSlot });
      if (persist) persistCartDelivery();
      else syncSummary();
      if (calendarWrap && !calendarWrap.hidden) renderMonthCalendar();
    }

    function renderDays() {
      if (!daysWrap) return;
      const min = minDateIso();
      const max = maxDateIso();
      const selected = dateInput.value;
      let start = min;

      if (selected && selected >= min && selected <= max) {
        let end = addStoreDays(min, dayCount - 1);
        if (selected > end) {
          start = addStoreDays(selected, -(dayCount - 1));
          if (start < min) start = min;
        }
      }

      daysWrap.innerHTML = '';
      for (let i = 0; i < dayCount; i++) {
        const iso = addStoreDays(start, i);
        if (iso > max) break;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'delivery-picker__day' + (selected === iso ? ' is-selected' : '');
        btn.dataset.deliveryDay = '';
        btn.dataset.date = iso;
        btn.setAttribute('role', 'option');
        btn.setAttribute('aria-selected', selected === iso ? 'true' : 'false');
        const isToday = iso === getStoreNow().iso;
        const showSameDay = isToday && sameDayAvailable();
        btn.innerHTML =
          '<span class="delivery-picker__day-name">' +
          dayLabel(iso) +
          '</span>' +
          '<span class="delivery-picker__day-date">' +
          '<span class="delivery-picker__day-num">' +
          dayNumber(iso) +
          '</span>' +
          '<span class="delivery-picker__day-month">' +
          dayMonth(iso) +
          '</span>' +
          '</span>' +
          (showSameDay ? '<span class="delivery-picker__day-badge">' + sameDayBadge + '</span>' : '');
        if (showSameDay) btn.classList.add('has-badge');
        btn.addEventListener('click', () => {
          closeMonthCalendar();
          setDate(iso);
        });
        daysWrap.appendChild(btn);
      }
    }

    function monthTitle(year, month) {
      const probe = new Date(Date.UTC(year, month - 1, 1, 12));
      return new Intl.DateTimeFormat(undefined, {
        timeZone: 'UTC',
        month: 'long',
        year: 'numeric',
      }).format(probe);
    }

    function weekdayLabels() {
      return Array.from({ length: 7 }, (_, i) => {
        const probe = new Date(Date.UTC(2024, 0, 7 + i, 12)); // Sunday-start week
        return new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', weekday: 'narrow' }).format(probe);
      });
    }

    function closeMonthCalendar() {
      if (!calendarWrap) return;
      calendarWrap.hidden = true;
      calendarWrap.innerHTML = '';
      root.classList.remove('is-calendar-open');
      qs('[data-delivery-calendar-backdrop]', root)?.remove();
      if (moreDateBtn) moreDateBtn.setAttribute('aria-expanded', 'false');
    }

    function ensureCalendarBackdrop() {
      let backdrop = qs('[data-delivery-calendar-backdrop]', root);
      if (backdrop) return backdrop;
      backdrop = document.createElement('button');
      backdrop.type = 'button';
      backdrop.className = 'delivery-picker__calendar-backdrop';
      backdrop.dataset.deliveryCalendarBackdrop = '';
      backdrop.setAttribute('aria-label', 'Close calendar');
      backdrop.addEventListener('click', closeMonthCalendar);
      root.appendChild(backdrop);
      return backdrop;
    }

    function renderMonthCalendar() {
      if (!calendarWrap) return;
      const min = minDateIso();
      const max = maxDateIso();
      const selected = dateInput.value || min;
      const selectedParts = storeDateFromISO(selected);
      if (!calendarView) {
        calendarView = { year: selectedParts.year, month: selectedParts.month };
      }

      const { year, month } = calendarView;
      const firstDow = new Date(Date.UTC(year, month - 1, 1, 12)).getUTCDay();
      const daysInMonth = new Date(Date.UTC(year, month, 0, 12)).getUTCDate();
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextYear = month === 12 ? year + 1 : year;
      const minParts = storeDateFromISO(min);
      const maxParts = storeDateFromISO(max);
      const canPrev =
        prevYear > minParts.year || (prevYear === minParts.year && prevMonth >= minParts.month);
      const canNext =
        nextYear < maxParts.year || (nextYear === maxParts.year && nextMonth <= maxParts.month);

      const weekdays = weekdayLabels()
        .map((label) => `<span class="delivery-picker__cal-dow">${label}</span>`)
        .join('');

      let cells = '';
      for (let i = 0; i < firstDow; i++) {
        cells += '<span class="delivery-picker__cal-day is-empty" aria-hidden="true"></span>';
      }
      for (let day = 1; day <= daysInMonth; day++) {
        const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const disabled = iso < min || iso > max;
        const on = iso === selected;
        cells += `<button type="button" class="delivery-picker__cal-day${on ? ' is-selected' : ''}${
          disabled ? ' is-disabled' : ''
        }" data-cal-date="${iso}" ${disabled ? 'disabled' : ''} aria-pressed="${on ? 'true' : 'false'}">${day}</button>`;
      }

      ensureCalendarBackdrop();
      root.classList.add('is-calendar-open');
      calendarWrap.hidden = false;
      calendarWrap.innerHTML = `
        <div class="delivery-picker__cal-head">
          <button type="button" class="delivery-picker__cal-nav" data-cal-prev ${canPrev ? '' : 'disabled'} aria-label="Previous month">‹</button>
          <p class="delivery-picker__cal-title">${monthTitle(year, month)}</p>
          <button type="button" class="delivery-picker__cal-nav" data-cal-next ${canNext ? '' : 'disabled'} aria-label="Next month">›</button>
        </div>
        <div class="delivery-picker__cal-grid" role="grid">
          ${weekdays}
          ${cells}
        </div>
      `;

      qs('[data-cal-prev]', calendarWrap)?.addEventListener('click', () => {
        if (!canPrev) return;
        calendarView = { year: prevYear, month: prevMonth };
        renderMonthCalendar();
      });
      qs('[data-cal-next]', calendarWrap)?.addEventListener('click', () => {
        if (!canNext) return;
        calendarView = { year: nextYear, month: nextMonth };
        renderMonthCalendar();
      });
      qsa('[data-cal-date]', calendarWrap).forEach((btn) => {
        btn.addEventListener('click', () => {
          const iso = btn.dataset.calDate;
          if (!iso) return;
          setDate(iso);
          renderDays();
          closeMonthCalendar();
        });
      });
    }

    function openMonthCalendar() {
      if (!calendarWrap) return;
      if (!calendarWrap.hidden) {
        closeMonthCalendar();
        return;
      }
      const selected = dateInput.value || minDateIso();
      const parts = storeDateFromISO(selected);
      calendarView = { year: parts.year, month: parts.month };
      if (moreDateBtn) moreDateBtn.setAttribute('aria-expanded', 'true');
      renderMonthCalendar();
    }

    dateInput.min = minDateIso();
    dateInput.max = maxDateIso();

    if (moreDateBtn) {
      moreDateBtn.setAttribute('aria-expanded', 'false');
      moreDateBtn.setAttribute('aria-controls', calendarWrap?.id || '');
      moreDateBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openMonthCalendar();
      });
    }

    dateInput.addEventListener('change', () => {
      renderDays();
      setDate(dateInput.value, { persist: !isCartDelivery, autoSlot: true });
    });

    root.dataset.committedDate = dateInput.value || '';
    root.dataset.committedTime = timeInput?.value || '';

    renderDays();
    if (!dateInput.value) {
      setDate(minDateIso(), { persist: false, autoSlot: true });
    } else {
      if (!timeInput.value) {
        timeInput.value = asapValue;
      }
      const iso = toIsoDate(dateInput.value) || dateInput.value;
      if (isDeliveryUnavailable(iso, timeInput.value)) {
        const next = earliestAvailableDelivery();
        if (timeInput) timeInput.value = next.time;
        setDate(next.date, { persist: false, autoSlot: true });
      } else {
        renderSlots({ autoSlot: false });
        syncSummary();
      }
    }
  }

  function initDeliveryPickers(scope = document) {
    qsa('[data-delivery-picker]', scope).forEach((el) => {
      delete el.dataset.ready;
      buildCalendar(el);
    });
  }


  /* ---------- product gallery ---------- */
  function attachLightboxSwipe(lightbox, stage, image, { isZoomed, onNavigate, canGo }) {
    if (!lightbox || !stage || !image) return { navigate() {} };

    let start = null;
    let dragging = false;
    let ignoreClick = false;
    let busy = false;
    const SWIPE_MIN = 56;
    const EASE = 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.28s ease';

    const rtl = () => document.documentElement.getAttribute('dir') === 'rtl';
    const allowed = (dir) => (typeof canGo === 'function' ? canGo(dir) : true);

    const setMotion = (x, opacity, animate) => {
      image.style.transition = animate ? EASE : 'none';
      image.style.transform = x ? `translate3d(${x}px, 0, 0)` : '';
      image.style.opacity = opacity == null ? '' : String(opacity);
    };

    const clearMotion = () => {
      image.style.transition = '';
      image.style.transform = '';
      image.style.opacity = '';
    };

    const navigate = (dir) => {
      if (!dir || busy || isZoomed() || !allowed(dir)) return false;
      busy = true;
      const width = Math.max(stage.clientWidth || 0, 280);
      const outX = dir > 0 ? -width : width;
      const inX = -outX;
      setMotion(outX, 0.28, true);

      let settled = false;
      const finishOut = () => {
        if (settled) return;
        settled = true;
        onNavigate(dir);
        setMotion(inX, 0.28, false);
        const reveal = () => {
          requestAnimationFrame(() => setMotion(0, 1, true));
        };
        if (image.complete) reveal();
        else image.addEventListener('load', reveal, { once: true });
        window.setTimeout(() => {
          clearMotion();
          busy = false;
        }, 380);
      };

      image.addEventListener('transitionend', finishOut, { once: true });
      window.setTimeout(finishOut, 320);
      return true;
    };

    stage.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('button')) return;
      start = {
        x: e.clientX,
        y: e.clientY,
        id: e.pointerId,
        zoomed: isZoomed(),
        scrollX: stage.scrollLeft,
        scrollY: stage.scrollTop,
      };
      dragging = false;
      if (start.zoomed) return;
      try {
        stage.setPointerCapture(e.pointerId);
      } catch (err) {
        /* Safari may throw if the pointer is already released */
      }
    });

    stage.addEventListener('pointermove', (e) => {
      if (!start || e.pointerId !== start.id || start.zoomed || busy) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (!dragging) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          start = null;
          return;
        }
        dragging = true;
      }
      const dir = rtl() ? (dx > 0 ? 1 : -1) : dx < 0 ? 1 : -1;
      const resisted = !allowed(dir) ? dx * 0.28 : dx;
      setMotion(resisted, 1 - Math.min(0.35, Math.abs(dx) / 900), false);
    });

    const finish = (e) => {
      if (!start || (e.pointerId != null && e.pointerId !== start.id)) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      const wasZoomed = start.zoomed;
      const panMoved =
        wasZoomed &&
        (Math.abs(dx) > 8 ||
          Math.abs(dy) > 8 ||
          Math.abs(stage.scrollLeft - start.scrollX) > 8 ||
          Math.abs(stage.scrollTop - start.scrollY) > 8);
      const wasDragging = dragging;
      start = null;
      dragging = false;

      if (wasZoomed) {
        if (panMoved) ignoreClick = true;
        return;
      }
      if (busy) return;

      const dir = rtl() ? (dx > 0 ? 1 : -1) : dx < 0 ? 1 : -1;
      if (!wasDragging || Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy) + 8 || !allowed(dir)) {
        setMotion(0, 1, true);
        window.setTimeout(clearMotion, 320);
        return;
      }
      ignoreClick = true;
      navigate(dir);
    };

    stage.addEventListener('pointerup', finish);
    stage.addEventListener('pointercancel', () => {
      start = null;
      dragging = false;
      if (!busy) clearMotion();
    });
    image.addEventListener(
      'click',
      (e) => {
        if (!ignoreClick) return;
        ignoreClick = false;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );

    return { navigate };
  }

  function initGalleries() {
    qsa('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.galleryReady === 'true') return;
      gallery.dataset.galleryReady = 'true';
      const main = qs('[data-gallery-main]', gallery);
      const track = qs('[data-gallery-track]', gallery);
      const slides = qsa('[data-gallery-slide]', gallery);
      const thumbs = qsa('[data-gallery-thumb]', gallery);
      const dots = qsa('[data-gallery-dot]', gallery);
      const countEl = qs('[data-gallery-count]', gallery);
      if (!main) return;

      let index = 0;
      const max = Math.max(0, slides.length - 1);
      const imageSlides = slides.filter((slide) => slide.dataset.full);

      const syncUi = (nextIndex) => {
        index = Math.max(0, Math.min(max, nextIndex));
        thumbs.forEach((thumb, i) => {
          const active = i === index;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        dots.forEach((dot, i) => {
          const active = i === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        if (countEl && slides.length > 1) {
          countEl.textContent = `${index + 1} / ${slides.length}`;
        }
      };

      const goTo = (nextIndex, behavior = 'smooth') => {
        syncUi(nextIndex);
        const slide = slides[index];
        if (!track || !slide) return;
        const start = slides[0] ? slides[0].offsetLeft : 0;
        track.scrollTo({ left: slide.offsetLeft - start, behavior });
      };

      thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => goTo(Number(thumb.dataset.index) || i));
      });
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => goTo(Number(dot.dataset.index) || i));
      });

      if (track && slides.length > 1) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const next = slides.indexOf(entry.target);
              if (next >= 0 && next !== index) syncUi(next);
            });
          },
          { root: track, threshold: 0.55 }
        );
        slides.forEach((slide) => io.observe(slide));
      }

      gallery.goToMedia = (mediaId) => {
        if (!mediaId) return false;
        const slideIndex = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
        if (slideIndex < 0) return false;
        goTo(slideIndex, 'auto');
        return true;
      };

      const lightbox = qs('[data-product-lightbox]', gallery);
      if (lightbox && lightbox.parentElement !== document.body) {
        document.body.appendChild(lightbox);
      }
      const lightboxImg = lightbox && qs('[data-product-lightbox-image]', lightbox);
      const lightboxCounter = lightbox && qs('[data-product-lightbox-counter]', lightbox);
      const lightboxStage = lightbox && qs('[data-product-lightbox-stage]', lightbox);
      const zoomBtn = lightbox && qs('[data-product-lightbox-zoom]', lightbox);
      let lightboxIndex = 0;
      let lastFocus = null;

      const resetZoom = () => {
        if (!lightbox) return;
        lightbox.classList.remove('is-zoomed');
        zoomBtn?.setAttribute('aria-pressed', 'false');
        if (!lightboxStage) return;
        lightboxStage.scrollTop = 0;
        lightboxStage.scrollLeft = 0;
        if (lightboxImg) {
          lightboxImg.style.transition = '';
          lightboxImg.style.transform = '';
          lightboxImg.style.opacity = '';
        }
      };

      const toggleZoom = (point) => {
        if (!lightbox || !lightboxStage || !lightboxImg) return;
        if (lightbox.classList.contains('is-zoomed')) {
          resetZoom();
          return;
        }
        const rect = lightboxImg.getBoundingClientRect();
        const ratioX = rect.width ? (point.clientX - rect.left) / rect.width : 0.5;
        const ratioY = rect.height ? (point.clientY - rect.top) / rect.height : 0.5;
        lightbox.classList.add('is-zoomed');
        zoomBtn?.setAttribute('aria-pressed', 'true');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            lightboxStage.scrollLeft = ratioX * lightboxStage.scrollWidth - lightboxStage.clientWidth / 2;
            lightboxStage.scrollTop = ratioY * lightboxStage.scrollHeight - lightboxStage.clientHeight / 2;
          });
        });
      };

      const centerPoint = () => {
        const rect = lightboxImg?.getBoundingClientRect();
        if (!rect) return { clientX: 0, clientY: 0 };
        return { clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2 };
      };

      const closeLightbox = () => {
        if (!lightbox || lightbox.hidden) return;
        resetZoom();
        lightbox.hidden = true;
        lightbox.setAttribute('aria-hidden', 'true');
        lightbox.classList.remove('is-open');
        document.documentElement.classList.remove('product-lightbox-open');
        document.body.classList.remove('product-lightbox-open');
        lockScroll(false);
        lastFocus?.focus?.();
      };

      const renderLightbox = (i) => {
        const imageMax = Math.max(0, imageSlides.length - 1);
        lightboxIndex = Math.max(0, Math.min(imageMax, i));
        const slide = imageSlides[lightboxIndex] || slides[lightboxIndex];
        if (!slide || !lightboxImg) return;
        resetZoom();
        lightboxImg.removeAttribute('srcset');
        lightboxImg.removeAttribute('sizes');
        lightboxImg.src = slide.dataset.full || slide.dataset.src || '';
        lightboxImg.alt = slide.dataset.alt || '';
        if (lightboxCounter) lightboxCounter.textContent = `${lightboxIndex + 1} / ${imageSlides.length}`;
        const galleryIndex = slides.indexOf(slide);
        if (galleryIndex >= 0) goTo(galleryIndex, 'auto');
      };

      const openLightbox = (i) => {
        if (!lightbox || !imageSlides.length) return;
        lastFocus = document.activeElement;
        renderLightbox(i);
        const showNav = imageSlides.length > 1;
        qsa('[data-product-lightbox-prev], [data-product-lightbox-next]', lightbox).forEach((btn) => {
          btn.hidden = !showNav;
        });
        if (lightboxCounter) lightboxCounter.hidden = !showNav;
        lightbox.hidden = false;
        lightbox.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('product-lightbox-open');
        document.body.classList.add('product-lightbox-open');
        lockScroll(true);
        requestAnimationFrame(() => {
          lightbox.classList.add('is-open');
          qs('[data-product-lightbox-close]', lightbox)?.focus();
        });
      };

      let gesture = null;
      const TAP_SLOP = 12;
      track?.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        gesture = { x: e.clientX, y: e.clientY, scroll: track.scrollLeft };
      });
      imageSlides.forEach((slide, i) => {
        slide.addEventListener('click', (e) => {
          if (gesture) {
            const moved =
              Math.abs(e.clientX - gesture.x) > TAP_SLOP ||
              Math.abs(e.clientY - gesture.y) > TAP_SLOP ||
              Math.abs((track?.scrollLeft || 0) - gesture.scroll) > TAP_SLOP;
            gesture = null;
            if (moved) {
              e.preventDefault();
              return;
            }
          }
          e.preventDefault();
          openLightbox(i);
        });
      });

      if (lightbox) {
        qsa('[data-product-lightbox-close]', lightbox).forEach((btn) => {
          btn.addEventListener('click', closeLightbox);
        });
        lightboxImg?.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleZoom(e);
        });
        lightboxImg?.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          toggleZoom(centerPoint());
        });
        zoomBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          toggleZoom(centerPoint());
        });
        const lightboxSwipe = attachLightboxSwipe(lightbox, lightboxStage, lightboxImg, {
          isZoomed: () => lightbox.classList.contains('is-zoomed'),
          canGo: (delta) => {
            const next = lightboxIndex + delta;
            return next >= 0 && next < imageSlides.length;
          },
          onNavigate: (delta) => renderLightbox(lightboxIndex + delta),
        });
        qs('[data-product-lightbox-prev]', lightbox)?.addEventListener('click', (e) => {
          e.preventDefault();
          lightboxSwipe.navigate(-1);
        });
        qs('[data-product-lightbox-next]', lightbox)?.addEventListener('click', (e) => {
          e.preventDefault();
          lightboxSwipe.navigate(1);
        });
        document.addEventListener('keydown', (e) => {
          if (!lightbox || lightbox.hidden) return;
          if (e.key === 'Escape') {
            if (lightbox.classList.contains('is-zoomed')) resetZoom();
            else closeLightbox();
            return;
          }
          if (e.key === 'ArrowLeft') lightboxSwipe.navigate(-1);
          if (e.key === 'ArrowRight') lightboxSwipe.navigate(1);
        });
      }
    });
  }

  function syncProductMedia(scope, mediaId, imageUrl) {
    if (!scope) return;
    const gallery = qs('[data-product-gallery]', scope) || qs('[data-product-gallery]');
    if (gallery?.goToMedia && mediaId && gallery.goToMedia(mediaId)) return;

    const qvGallery = qs('[data-qv-gallery]', scope);
    if (qvGallery?.goToMedia && mediaId && qvGallery.goToMedia(mediaId)) return;

    if (imageUrl) {
      const main = qs('[data-gallery-main]', gallery || scope);
      if (main) {
        main.innerHTML = `<img src="${imageUrl}" alt="" width="800" height="800" loading="eager" decoding="async">`;
      }
    }
  }

  /* ---------- option-based variant picker ---------- */
  function initVariantPickers(root = document) {
    qsa('[data-variant-picker]', root).forEach((picker) => {
      if (picker.dataset.variantPickerReady === 'true') return;
      picker.dataset.variantPickerReady = 'true';

      const jsonEl = qs('[data-product-variants]', picker);
      if (!jsonEl) return;
      let variants = [];
      try {
        variants = JSON.parse(jsonEl.textContent || '[]');
      } catch (e) {
        return;
      }
      if (!variants.length) return;

      const form = picker.closest('form');
      const scope = form || picker.closest('[data-quick-view-content]') || picker.closest('.product') || document;
      const optionGroups = qsa('[data-option-index]', picker);

      const getSelectedOptions = () =>
        optionGroups.map((group) => {
          const checked = qs('input[type="radio"]:checked', group);
          return checked ? checked.value : null;
        });

      const findVariant = (selected) =>
        variants.find((variant) =>
          selected.every((value, index) => {
            if (value == null) return true;
            return String(variant[`option${index + 1}`] || '') === String(value);
          })
        );

      const updateAvailability = (selected) => {
        optionGroups.forEach((group, groupIndex) => {
          qsa('[data-option-value]', group).forEach((pill) => {
            const value = pill.dataset.optionValue;
            const probe = selected.slice();
            probe[groupIndex] = value;
            const match = variants.find((variant) =>
              probe.every((val, index) => {
                if (val == null) return true;
                return String(variant[`option${index + 1}`] || '') === String(val);
              })
            );
            const available = Boolean(match?.available);
            const input = qs('input', pill);
            pill.classList.toggle('is-unavailable', !available);
            if (input) input.disabled = !available;
          });
        });
      };

      const formatVariantPriceHtml = (variant) => {
        if (!variant?.price_money) return '';
        const saleLabel = window.theme?.strings?.salePrice || 'Sale price';
        const regularLabel = window.theme?.strings?.regularPrice || 'Regular price';
        if (variant.compare_at_money) {
          return `<div class="price" data-product-price><span class="price__sale"><span class="visually-hidden">${saleLabel}</span>${variant.price_money}</span><span class="price__compare"><span class="visually-hidden">${regularLabel}</span>${variant.compare_at_money}</span></div>`;
        }
        return `<div class="price" data-product-price><span class="price__regular">${variant.price_money}</span></div>`;
      };

      const syncStickyPurchase = (variant) => {
        const available = variant.available !== false;
        const label = available
          ? window.theme?.strings?.addToCart || 'Add to cart'
          : window.theme?.strings?.soldOut || 'Sold out';
        const stickyBtn = qs('[data-sticky-atc-btn]');
        const stickyLabel = qs('[data-sticky-atc-label]');
        const stickyPrices = qsa('[data-sticky-price]');
        const stickyBar = qs('[data-sticky-atc]');

        if (stickyBtn) stickyBtn.disabled = !available;
        if (stickyLabel) stickyLabel.textContent = label;
        if (variant.price_money) stickyPrices.forEach((el) => { el.textContent = variant.price_money; });
        if (stickyBar) {
          stickyBar.classList.toggle('sticky-atc--soldout', !available);
          stickyBar.classList.toggle('sticky-atc--unavailable', !available);
        }
      };

      const applyVariant = (variant) => {
        if (!variant) return;
        const idInput =
          (form && (qs('[name="id"]', form) || qs('[data-qv-variant-id]', form))) ||
          qs('[name="id"]', scope) ||
          qs('[data-qv-variant-id]', scope);
        if (idInput) idInput.value = variant.id;

        const priceHtml = formatVariantPriceHtml(variant);
        if (priceHtml) {
          const priceRoots = new Set();
          if (scope) priceRoots.add(scope);
          const productEl = (form || picker).closest('.product');
          const qvEl = (form || picker).closest('[data-quick-view-content]');
          if (productEl) priceRoots.add(productEl);
          if (qvEl) priceRoots.add(qvEl);

          priceRoots.forEach((root) => {
            qsa('[data-product-price]', root).forEach((el) => {
              const tmp = document.createElement('template');
              tmp.innerHTML = priceHtml.trim();
              const next = tmp.content.firstElementChild;
              if (next) el.replaceWith(next);
            });
          });
        }

        qsa('[data-selected-option]', scope).forEach((el) => {
          el.textContent = variant.title || '';
        });

        const available = variant.available !== false;
        const atc =
          qs('[data-add-to-cart]', form) ||
          qs('.qv__atc', form) ||
          qs('[data-add-to-cart]', scope) ||
          qs('.qv__atc', scope);
        const label = available
          ? window.theme?.strings?.addToCart || 'Add to cart'
          : window.theme?.strings?.soldOut || 'Sold out';
        syncAtcButton(atc, {
          available,
          label,
          price: variant.price_money || null,
        });
        syncStickyPurchase(variant);
        syncFbtMainVariant(variant);
        const deliveryRoot = (form || picker)?.closest('.product') || scope || document;
        qsa('[data-product-delivery-purchase]', deliveryRoot).forEach((el) => {
          el.hidden = !available;
        });
        const productEl = (form || picker).closest('.product');
        const qvEl = (form || picker).closest('[data-quick-view-content]');
        syncPurchaseLinks(qvEl || productEl || scope, {
          available,
          variantId: variant.id,
        });

        const status = qs('[data-variant-status]', picker);
        if (status) {
          status.textContent = available ? variant.title : `${variant.title} — sold out`;
        }

        syncProductMedia(scope, variant.featured_media_id, variant.featured_image);

        // Keep URL in sync on PDP (not quick view)
        if (form && !form.hasAttribute('data-quick-view-form') && variant.id && window.history?.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      };

      optionGroups.forEach((group) => {
        qsa('input[type="radio"]', group).forEach((input) => {
          input.addEventListener('change', () => {
            if (!input.checked) return;
            const pill = input.closest('[data-option-value]');
            qsa('[data-option-value]', group).forEach((p) => p.classList.remove('is-selected'));
            if (pill) pill.classList.add('is-selected');
            const selectedLabel = qs('[data-option-selected]', group);
            if (selectedLabel) selectedLabel.textContent = input.value;

            const selected = getSelectedOptions();
            updateAvailability(selected);
            const variant = findVariant(selected) || variants.find((v) => v.available) || variants[0];
            applyVariant(variant);
          });
        });
      });

      updateAvailability(getSelectedOptions());
    });
  }

  /* ---------- product card color swatches ---------- */
  function initCardSwatches() {
    if (document.documentElement.dataset.cardSwatchesReady === 'true') return;
    document.documentElement.dataset.cardSwatchesReady = 'true';

    const applySwatch = (swatch) => {
      const list = swatch.closest('[data-card-swatches]');
      const card = swatch.closest('.product-card');
      if (!list || !card) return;

      const img = qs('.product-card__image--primary', card);
      const imageLink = qs('.product-card__image-link', card);
      const titleLink = qs('.product-card__title a', card);
      const src = swatch.getAttribute('data-image-src');

      if (img && src) {
        img.src = src;
        const srcset = swatch.getAttribute('data-image-srcset');
        if (srcset) img.srcset = srcset;
        const sizes = swatch.getAttribute('data-image-sizes');
        if (sizes) img.sizes = sizes;
        const alt = swatch.getAttribute('data-image-alt');
        if (alt) img.alt = alt;
        card.classList.add('is-swatch-preview');
      }

      qsa('[data-card-swatch]', list).forEach((el) => {
        el.classList.toggle('is-selected', el === swatch);
      });

      const href = swatch.getAttribute('href');
      if (href) {
        if (imageLink) imageLink.setAttribute('href', href);
        if (titleLink) titleLink.setAttribute('href', href);
      }
    };

    document.addEventListener('pointerover', (e) => {
      const swatch = e.target.closest('[data-card-swatch]');
      if (!swatch) return;
      applySwatch(swatch);
    });

    document.addEventListener('focusin', (e) => {
      const swatch = e.target.closest('[data-card-swatch]');
      if (!swatch) return;
      applySwatch(swatch);
    });
  }

  /* ---------- legacy variant size cards ---------- */
  function initVariantCards() {
    qsa('[data-variant-cards]').forEach((group) => {
      if (group.dataset.variantCardsReady === 'true') return;
      group.dataset.variantCardsReady = 'true';
      const form = group.closest('form');
      const scope = form || group.closest('.qv') || document;
      const cards = qsa('.size-card', group);
      cards.forEach((card) => {
        card.addEventListener('click', () => {
          if (card.classList.contains('is-unavailable')) return;
          cards.forEach((c) => c.classList.remove('is-selected'));
          card.classList.add('is-selected');
          const input = qs('input', card);
          if (input) input.checked = true;
          const variantId = card.dataset.variantId;
          const price = card.dataset.price;
          const available = card.dataset.available !== 'false';
          const title = card.dataset.title || '';
          if (form) {
            const idInput = qs('[name="id"]', form) || qs('[data-qv-variant-id]', form);
            if (idInput && variantId) idInput.value = variantId;
          }
          qsa('[data-product-price]', scope).forEach((el) => {
            if (price) el.innerHTML = `<span class="price"><span class="price__regular">${price}</span></span>`;
          });
          qsa('[data-selected-option]', scope).forEach((el) => {
            if (title) el.textContent = title;
          });
          const atc =
            qs('[data-add-to-cart]', form) ||
            qs('.qv__atc', form) ||
            qs('[data-add-to-cart]', scope) ||
            qs('.qv__atc', scope);
          const stickyBtn = qs('[data-sticky-atc-btn]');
          const stickyLabel = qs('[data-sticky-atc-label]');
          const stickyPrices = qsa('[data-sticky-price]');
          const stickyBar = qs('[data-sticky-atc]');
          const label = available
            ? window.theme?.strings?.addToCart || 'Add to cart'
            : window.theme?.strings?.soldOut || 'Sold out';
          syncAtcButton(atc, { available, label, price: price || null });
          if (stickyBtn) stickyBtn.disabled = !available;
          if (stickyLabel) stickyLabel.textContent = label;
          if (price) stickyPrices.forEach((el) => { el.textContent = price; });
          if (stickyBar) stickyBar.classList.toggle('sticky-atc--soldout', !available);
          const productEl = (form || group).closest('.product');
          const qvEl = (form || group).closest('[data-quick-view-content]');
          syncPurchaseLinks(qvEl || productEl || scope, {
            available,
            variantId,
          });
        });
      });
    });
  }

  function initQuantityButtons() {
    qsa('[data-quantity], .quantity').forEach((wrap) => {
      if (wrap.dataset.qtyReady === 'true') return;
      wrap.dataset.qtyReady = 'true';

      const input = qs('[data-qty-input], .quantity__input', wrap);
      const down = qs('[data-qty-down]', wrap);
      const up = qs('[data-qty-up]', wrap);
      if (!input) return;

      const syncLimits = () => {
        const min = Number(input.min || 1);
        const max = Number(input.max || 99);
        let value = Number(input.value || min);
        if (Number.isNaN(value)) value = min;
        value = Math.min(max, Math.max(min, value));
        input.value = String(value);
        if (down) down.disabled = value <= min;
        if (up) up.disabled = value >= max;
      };

      const bump = () => {
        input.classList.remove('is-bump');
        // force reflow so animation can replay
        void input.offsetWidth;
        input.classList.add('is-bump');
      };

      const step = (dir) => {
        if (dir < 0) input.stepDown();
        else input.stepUp();
        syncLimits();
        bump();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      if (down) {
        down.addEventListener('click', () => {
          if (down.disabled) return;
          step(-1);
        });
      }
      if (up) {
        up.addEventListener('click', () => {
          if (up.disabled) return;
          step(1);
        });
      }

      input.addEventListener('change', syncLimits);
      input.addEventListener('input', syncLimits);
      syncLimits();
    });
  }

  function initCharCounters() {
    qsa('[data-char-count-input]').forEach((input) => {
      if (input.dataset.charReady === 'true') return;
      input.dataset.charReady = 'true';
      const counter = qs(`[data-char-count][data-for="${input.id}"]`) || qs('[data-char-count]', input.closest('.field'));
      if (!counter) return;
      const max = Number(input.getAttribute('maxlength')) || 250;
      const update = () => {
        counter.textContent = `${input.value.length}/${max}`;
      };
      input.addEventListener('input', update);
      update();
    });
  }

  function initQuickViewGallery(root) {
    if (!root) return;
    const gallery = qs('[data-qv-gallery]', root);
    if (!gallery) return;

    const track = qs('[data-qv-carousel-track]', gallery);
    const slides = qsa('[data-qv-slide]', gallery);
    const dots = qsa('[data-qv-dot]', gallery);
    const counter = qs('[data-qv-counter]', gallery);
    const prevBtn = qs('[data-qv-prev]', gallery);
    const nextBtn = qs('[data-qv-next]', gallery);
    if (!track || !slides.length) return;

    let index = 0;
    const max = slides.length - 1;

    const sync = (nextIndex, behavior = 'smooth') => {
      index = Math.max(0, Math.min(max, nextIndex));
      const slide = slides[index];
      if (slide) {
        const left = slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2;
        track.scrollTo({ left: Math.max(0, left), behavior });
      }
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
    };

    const go = (delta) => sync(index + delta);

    prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      go(-1);
    });
    nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      go(1);
    });
    dots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        sync(Number(dot.dataset.index) || 0);
      });
    });

    let scrollTimer;
    track.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          const center = track.scrollLeft + track.clientWidth / 2;
          let nearest = 0;
          let nearestDist = Infinity;
          slides.forEach((slide, i) => {
            const mid = slide.offsetLeft + slide.clientWidth / 2;
            const dist = Math.abs(mid - center);
            if (dist < nearestDist) {
              nearestDist = dist;
              nearest = i;
            }
          });
          if (nearest !== index) {
            index = nearest;
            dots.forEach((dot, i) => {
              const active = i === index;
              dot.classList.toggle('is-active', active);
              dot.setAttribute('aria-selected', active ? 'true' : 'false');
            });
            if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
          }
        }, 60);
      },
      { passive: true }
    );

    const lightbox = qs('[data-qv-lightbox]');
    const lightboxImg = qs('[data-qv-lightbox-image]', lightbox);
    const lightboxCounter = qs('[data-qv-lightbox-counter]', lightbox);
    const lightboxStage = qs('[data-qv-lightbox-stage]', lightbox) || qs('.qv-lightbox__stage', lightbox);
    const quickViewModal = lightbox?.closest('#QuickViewModal');
    let lightboxIndex = 0;

    const zoomBtn = qs('[data-qv-lightbox-zoom]', lightbox);

    const resetZoom = () => {
      if (!lightbox) return;
      lightbox.classList.remove('is-zoomed');
      zoomBtn?.setAttribute('aria-pressed', 'false');
      if (!lightboxStage) return;
      lightboxStage.scrollTop = 0;
      lightboxStage.scrollLeft = 0;
      if (lightboxImg) {
        lightboxImg.style.transition = '';
        lightboxImg.style.transform = '';
        lightboxImg.style.opacity = '';
      }
    };

    // Zoom toward the clicked point so the detail under the cursor stays put.
    const toggleZoom = (point) => {
      if (!lightbox || !lightboxStage || !lightboxImg) return;
      if (lightbox.classList.contains('is-zoomed')) {
        resetZoom();
        return;
      }
      const rect = lightboxImg.getBoundingClientRect();
      const ratioX = rect.width ? (point.clientX - rect.left) / rect.width : 0.5;
      const ratioY = rect.height ? (point.clientY - rect.top) / rect.height : 0.5;
      lightbox.classList.add('is-zoomed');
      zoomBtn?.setAttribute('aria-pressed', 'true');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          lightboxStage.scrollLeft = ratioX * lightboxStage.scrollWidth - lightboxStage.clientWidth / 2;
          lightboxStage.scrollTop = ratioY * lightboxStage.scrollHeight - lightboxStage.clientHeight / 2;
        });
      });
    };

    const centerPoint = () => {
      const rect = lightboxImg?.getBoundingClientRect();
      if (!rect) return { clientX: 0, clientY: 0 };
      return { clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2 };
    };

    const closeLightbox = () => {
      if (!lightbox) return;
      resetZoom();
      lightbox.hidden = true;
      lightbox.setAttribute('aria-hidden', 'true');
      lightbox.classList.remove('is-open');
      quickViewModal?.classList.remove('qv-lightbox-open');
    };

    const renderLightbox = (i) => {
      lightboxIndex = Math.max(0, Math.min(max, i));
      const slide = slides[lightboxIndex];
      if (!slide || !lightboxImg) return;
      resetZoom();
      // Always load the full-res asset — lightbox is a zoom surface, not a card thumbnail.
      lightboxImg.removeAttribute('srcset');
      lightboxImg.removeAttribute('sizes');
      lightboxImg.src = slide.dataset.full || slide.dataset.src || '';
      lightboxImg.alt = slide.dataset.alt || '';
      if (lightboxCounter) lightboxCounter.textContent = `${lightboxIndex + 1} / ${slides.length}`;
      sync(lightboxIndex, 'auto');
    };

    const openLightbox = (i) => {
      if (!lightbox || slides.length < 1) return;
      renderLightbox(i);
      const showNav = slides.length > 1;
      qsa('[data-qv-lightbox-prev], [data-qv-lightbox-next]', lightbox).forEach((btn) => {
        btn.hidden = !showNav;
      });
      if (lightboxCounter) lightboxCounter.hidden = !showNav;
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      quickViewModal?.classList.add('qv-lightbox-open');
      requestAnimationFrame(() => lightbox.classList.add('is-open'));
    };

    slides.forEach((slide) => {
      slide.addEventListener('click', (e) => {
        e.preventDefault();
        openLightbox(Number(slide.dataset.index) || 0);
      });
    });

    if (lightbox) {
      qsa('[data-qv-lightbox-close]', lightbox).forEach((btn) => {
        btn.addEventListener('click', closeLightbox);
      });
      lightboxImg?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleZoom(e);
      });
      lightboxImg?.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        toggleZoom(centerPoint());
      });
      zoomBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        toggleZoom(centerPoint());
      });
      const qvLightboxSwipe = attachLightboxSwipe(lightbox, lightboxStage, lightboxImg, {
        isZoomed: () => lightbox.classList.contains('is-zoomed'),
        canGo: (delta) => {
          const next = lightboxIndex + delta;
          return next >= 0 && next <= max;
        },
        onNavigate: (delta) => renderLightbox(lightboxIndex + delta),
      });
      qs('[data-qv-lightbox-prev]', lightbox)?.addEventListener('click', (e) => {
        e.preventDefault();
        qvLightboxSwipe.navigate(-1);
      });
      qs('[data-qv-lightbox-next]', lightbox)?.addEventListener('click', (e) => {
        e.preventDefault();
        qvLightboxSwipe.navigate(1);
      });
    }

    const onKey = (e) => {
      if (!lightbox || lightbox.hidden) return;
      if (e.key === 'Escape') {
        if (lightbox.classList.contains('is-zoomed')) resetZoom();
        else closeLightbox();
        return;
      }
      if (e.key === 'ArrowLeft') qvLightboxSwipe.navigate(-1);
      if (e.key === 'ArrowRight') qvLightboxSwipe.navigate(1);
    };
    document.addEventListener('keydown', onKey);

    // Reset when quick view content is replaced
    gallery._qvCleanup = () => {
      document.removeEventListener('keydown', onKey);
      closeLightbox();
    };

    sync(0, 'auto');

    gallery.goToMedia = (mediaId) => {
      if (!mediaId) return false;
      const slideIndex = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (slideIndex < 0) return false;
      sync(slideIndex, 'smooth');
      return true;
    };
  }

  /* ---------- sticky ATC ---------- */
  function initStickyAtc() {
    const bar = qs('[data-sticky-atc]');
    const buybox = qs('[data-product-buybox]');
    const atc = buybox && (qs('[data-add-to-cart]', buybox) || qs('button[type="submit"][name="add"]', buybox));
    if (!bar || !buybox) return;

    const stickyBtn = qs('[data-sticky-atc-btn]', bar);
    const stickyLabel = qs('[data-sticky-atc-label]', bar);
    const mobileMq = window.matchMedia('(max-width: 749px)');

    // Writing a class always queues a mutation record, even when the value is
    // unchanged, so only touch the DOM on real transitions. Otherwise the
    // observer below would re-enter update() forever and lock up the page.
    let lastShow = null;
    const applyState = (show) => {
      if (lastShow === show) return;
      lastShow = show;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.body.classList.toggle('sticky-atc-active', show);
      document.documentElement.classList.toggle('sticky-atc-active', show);
    };

    const update = () => {
      if (
        document.documentElement.classList.contains('overlay-open') ||
        document.documentElement.classList.contains('product-lightbox-open')
      ) {
        applyState(false);
        return;
      }

      const target = atc || buybox;
      const rect = target.getBoundingClientRect();
      const scrolledPast = rect.bottom < 12;
      const canPurchase = !(atc && atc.disabled);
      // Phones keep the bar docked the whole time; larger screens only need it
      // once the in-page button scrolls away.
      applyState(canPurchase && (mobileMq.matches || scrolledPast));
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    if (mobileMq.addEventListener) mobileMq.addEventListener('change', update);
    else if (mobileMq.addListener) mobileMq.addListener(update);

    let lastOverlay = document.documentElement.classList.contains('overlay-open');
    const observer = new MutationObserver(() => {
      const overlayOpen = document.documentElement.classList.contains('overlay-open');
      if (overlayOpen === lastOverlay) return;
      lastOverlay = overlayOpen;
      update();
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        if (stickyBtn.disabled) return;
        const form = qs('#ProductForm') || qs('[data-product-form]');
        if (form) form.requestSubmit();
      });
    }

    if (atc && stickyBtn) {
      const readAtcLabel = () => {
        // The in-page button gains spinner/check nodes, so prefer its label wrapper.
        const wrap = qs('[data-atc-label-wrap]', atc);
        return (wrap || atc).textContent.trim();
      };
      const syncState = () => {
        stickyBtn.disabled = atc.disabled;
        bar.classList.toggle('sticky-atc--soldout', atc.disabled);
        const label = readAtcLabel();
        if (stickyLabel && label && stickyLabel.textContent.trim() !== label) {
          stickyLabel.textContent = label;
        }
        update();
      };
      const atcObserver = new MutationObserver(syncState);
      atcObserver.observe(atc, { attributes: true, childList: true, characterData: true, subtree: true });
      syncState();
    }
  }

  /* ---------- quick view ---------- */
  async function openQuickView(handle) {
    const modal = qs('#QuickViewModal');
    const body = qs('[data-quick-view-body]', modal);
    if (!modal || !body) return;
    const loadingLabel = window.theme?.strings?.loading || 'Loading…';
    body.innerHTML = `
      <div class="qv-dialog__loading" role="status">
        <span class="qv-dialog__spinner" aria-hidden="true"></span>
        <p>${loadingLabel}</p>
      </div>
    `;
    openOverlay(modal);
    try {
      // Keep the locale prefix (/ar/...) so the section renders in the active language.
      const root = (settings.routes?.root || '/').replace(/\/$/, '');
      const res = await fetch(`${root}/products/${handle}?section_id=quick-view`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-quick-view-content]');
      body.innerHTML = content ? content.innerHTML : '<p class="qv-dialog__loading">Unable to load product.</p>';
      initVariantCards();
      initVariantPickers(body);
      initQuickViewGallery(body);
    } catch (err) {
      body.innerHTML = '<p class="qv-dialog__loading">Unable to load product.</p>';
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    openQuickView(btn.dataset.quickView);
  });

  /* ---------- filters (collection form sync) ---------- */
  function syncFilterDrawerToForm() {
    const form = qs('#FacetFiltersForm') || qs('[data-filter-form]');
    const drawerBody = qs('[data-filter-drawer-body]');
    if (!form || !drawerBody) return form;

    qsa('[data-sync-filter]', drawerBody).forEach((el) => {
      const input = document.getElementById(el.getAttribute('data-sync-filter'));
      if (input) input.checked = el.checked;
    });

    qsa('[data-sync-filter-name]', drawerBody).forEach((el) => {
      const name = el.getAttribute('data-sync-filter-name');
      if (!name) return;
      const input = form.querySelector(`[name="${CSS.escape(name)}"]`);
      if (input) input.value = el.value;
    });

    return form;
  }

  document.addEventListener('change', (e) => {
    if (!e.target.closest('[data-filter-drawer-body]')) return;
    syncFilterDrawerToForm();
  });

  document.addEventListener('click', (e) => {
    const apply = e.target.closest('[data-filter-apply]');
    if (apply) {
      e.preventDefault();
      const form = syncFilterDrawerToForm();
      if (form) form.submit();
      return;
    }
    const clear = e.target.closest('[data-filter-clear]');
    if (clear) {
      e.preventDefault();
      const explicit = (clear.getAttribute('data-clear-url') || '').trim();
      // Stay on the current collection/search path — never bounce to /collections/all by default.
      const fallback = window.location.pathname || '/';
      const url = explicit || fallback;
      window.location.href = url;
    }
  });

  /* ---------- scroll animations ---------- */
  function revealAnimated(root = document) {
    qsa('[data-animate]', root).forEach((el) => el.classList.add('is-visible'));
  }

  function initAnimations() {
    const skipMotion =
      !settings.animationsEnabled || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);

    if (skipMotion || designMode) {
      revealAnimated();
      if (designMode) {
        document.addEventListener('shopify:section:load', (event) => revealAnimated(event.target));
      }
      return;
    }

    // Keep above-the-fold content painted for LCP/FCP before gating motion.
    const fold = window.innerHeight * 1.05;
    qsa('[data-animate]').forEach((el) => {
      if (el.getBoundingClientRect().top < fold) {
        el.classList.add('is-visible');
      }
    });
    document.documentElement.classList.add('animations-ready');

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );
    qsa('[data-animate]').forEach((el) => {
      if (!el.classList.contains('is-visible')) io.observe(el);
    });
    document.addEventListener('shopify:section:load', (event) => {
      qsa('[data-animate]', event.target).forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight * 1.05) {
          el.classList.add('is-visible');
        } else {
          io.observe(el);
        }
      });
    });
  }

  /* ---------- header scroll state ---------- */
  function initHeader() {
    const header = qs('[data-site-header]');
    if (!header) return;
    const group = header.closest('.shopify-section-group-header-group') || header;

    function syncHeaderHeight() {
      const height = Math.ceil(group.getBoundingClientRect().height) || group.offsetHeight;
      if (!height) return;
      const next = `${height}px`;
      if (document.documentElement.style.getPropertyValue('--header-height') !== next) {
        document.documentElement.style.setProperty('--header-height', next);
      }
    }

    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      syncHeaderHeight();
    };
    onScroll();
    syncHeaderHeight();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', syncHeaderHeight, { passive: true });
  }

  /* ---------- header navigation (mega + dropdown) ---------- */
  function initHeaderNav() {
    const header = qs('[data-site-header]');
    const nav = qs('[data-mega-nav]');
    if (!header || !nav) return;

    function closeAll() {
      qsa('.desktop-nav__item.is-open', nav).forEach((item) => {
        item.classList.remove('is-open');
        const btn = qs('[data-nav-toggle]', item);
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
      qsa('[data-nav-panel]', nav).forEach((panel) => {
        panel.hidden = true;
      });
      header.classList.remove('is-mega-open');
    }

    function openItem(item) {
      const btn = qs('[data-nav-toggle]', item);
      if (!btn) return;
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      closeAll();
      item.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      if (panel) panel.hidden = false;
      if (item.classList.contains('has-mega')) {
        header.classList.add('is-mega-open');
      }
    }

    nav.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-nav-toggle]');
      if (!toggle) return;
      e.preventDefault();
      const item = toggle.closest('.desktop-nav__item');
      if (item.classList.contains('is-open')) {
        closeAll();
      } else {
        openItem(item);
      }
    });

    let closeTimer;
    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(closeAll, 160);
    }
    function cancelClose() {
      clearTimeout(closeTimer);
    }

    qsa('.desktop-nav__item.has-submenu', nav).forEach((item) => {
      item.addEventListener('mouseenter', () => {
        cancelClose();
        openItem(item);
      });
      item.addEventListener('mouseleave', scheduleClose);
    });

    qsa('[data-nav-panel]', nav).forEach((panel) => {
      panel.addEventListener('mouseenter', cancelClose);
      panel.addEventListener('mouseleave', scheduleClose);
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-mega-nav]')) closeAll();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAll();
    });
  }

  function initAccountMenu() {
    const root = qs('[data-account-menu]');
    if (!root) return;

    const toggle = qs('[data-account-menu-toggle]', root);
    const panel = qs('[data-account-menu-panel]', root);
    if (!toggle || !panel) return;

    function close() {
      root.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
    }

    function open() {
      root.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
    }

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (root.classList.contains('is-open')) {
        close();
      } else {
        open();
      }
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-account-menu]')) close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });

    const megaNav = qs('[data-mega-nav]');
    if (megaNav) {
      megaNav.addEventListener('mouseenter', close);
      megaNav.addEventListener('focusin', close);
    }
  }

  function initMobileAccordions() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-accordion-toggle]');
      if (!btn) return;
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      const open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open));
      panel.hidden = open;
    });
  }

  function hasArabicText(str) {
    return /[\u0600-\u06FF]/.test(String(str || ''));
  }

  function isArabicLocale() {
    const locale = String(settings.locale || document.documentElement.lang || '').toLowerCase();
    return locale === 'ar' || locale.startsWith('ar-');
  }

  function withStoreLocalePath(url) {
    if (!url) return url;
    const root = String(settings.routes?.root || '/').replace(/\/$/, '');
    try {
      const parsed = new URL(url, window.location.origin);
      if (root && parsed.pathname !== root && !parsed.pathname.startsWith(`${root}/`)) {
        parsed.pathname = `${root}${parsed.pathname}`;
      }
      return `${parsed.pathname}${parsed.search}`;
    } catch (err) {
      return url;
    }
  }

  function predictiveFields() {
    return 'title,body,product_type,variants.title,vendor,tag';
  }

  function emptyPredictivePayload() {
    return { resources: { results: { products: [], collections: [], queries: [] } } };
  }

  async function fetchPredictiveResults(endpoint, query, limit) {
    if (!endpoint) return emptyPredictivePayload();
    try {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection,query');
      url.searchParams.set('resources[limit]', String(limit || 8));
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', predictiveFields());
      const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!res.ok) return emptyPredictivePayload();
      const data = await res.json();
      return data?.resources ? data : emptyPredictivePayload();
    } catch (err) {
      return emptyPredictivePayload();
    }
  }

  function predictiveEndpointForLocale() {
    const localeEndpoint = settings.routes?.predictiveSearch;
    const primaryEndpoint = settings.routes?.predictiveSearchPrimary;
    if (isArabicLocale() && primaryEndpoint) return primaryEndpoint;
    return localeEndpoint;
  }

  function productHandleFromItem(item) {
    const path = String(item?.url || '');
    const match = path.match(/\/products\/([^/?#]+)/);
    return (match && match[1]) || item?.handle || '';
  }

  async function fetchLocaleSearchProducts(query) {
    if (!isArabicLocale() || !settings.routes?.search) return [];
    try {
      const url = new URL(settings.routes.search, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('view', 'ajax');
      const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      return [];
    }
  }

  function applyLocaleProductTitles(payload, localeProducts) {
    const suggestProducts = payload?.resources?.results?.products || [];
    const suggestByHandle = new Map();
    suggestProducts.forEach((product) => {
      const handle = productHandleFromItem(product);
      if (handle) suggestByHandle.set(handle, product);
    });

    const out = [];
    const seen = new Set();

    (localeProducts || []).forEach((item) => {
      if (!item?.handle || seen.has(item.handle)) return;
      seen.add(item.handle);
      const suggest = suggestByHandle.get(item.handle);
      if (suggest) {
        out.push({
          ...suggest,
          title: item.title || suggest.title,
          url: item.url || suggest.url
        });
        return;
      }
      out.push({
        title: item.title,
        url: item.url,
        price: item.price,
        compare_at_price: item.compare_at_price,
        available: item.available,
        featured_image: item.featured_image
      });
    });

    suggestProducts.forEach((product) => {
      const handle = productHandleFromItem(product);
      if (!handle || seen.has(handle)) return;
      seen.add(handle);
      out.push(product);
    });

    if (payload?.resources?.results) {
      payload.resources.results.products = out.slice(0, 8);
    }
    return payload;
  }

  function mergePredictivePayload(primary, extra) {
    const first = primary?.resources?.results || {};
    const second = extra?.resources?.results || {};

    function mergeList(a, b, keyFn) {
      const seen = new Set();
      const out = [];
      [...(a || []), ...(b || [])].forEach((item) => {
        const key = keyFn ? keyFn(item) : item.url || item.handle || item.id || item.text;
        if (!key || seen.has(key)) return;
        seen.add(key);
        out.push({ ...item, url: withStoreLocalePath(item.url) });
      });
      return out;
    }

    function productKey(item) {
      const path = String(item.url || '');
      const match = path.match(/\/products\/([^/?#]+)/);
      return (match && match[1]) || item.handle || item.url || item.id;
    }

    return {
      resources: {
        results: {
          products: mergeList(first.products, second.products, productKey),
          collections: mergeList(first.collections, second.collections),
          queries: mergeList(first.queries, second.queries)
        }
      }
    };
  }

  /* ---------- predictive search ---------- */
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function initPredictiveSearch() {
    const modal = qs('#SearchModal');
    if (!modal || !settings.routes?.predictiveSearch) return;

    const input = qs('[data-search-input]', modal);
    const clearBtn = qs('[data-search-clear]', modal);
    const idle = qs('[data-search-idle]', modal);
    const status = qs('[data-search-status]', modal);
    const results = qs('[data-search-results]', modal);
    const empty = qs('[data-search-empty]', modal);
    const emptyMessage = qs('[data-search-empty-message]', modal);
    const emptyChips = qs('[data-search-empty-chips]', modal);
    const popularChips = qs('[data-search-popular-chips]', modal);
    if (!input || !results) return;

    const strings = {
      loading: modal.dataset.searchLoading || 'Searching…',
      noResults: modal.dataset.searchNoResults || 'No results for “[[terms]]”',
      tryThese: modal.dataset.searchTryThese || 'Try these instead',
      products: modal.dataset.searchProducts || 'Products',
      collections: modal.dataset.searchCollections || 'Collections',
      suggestions: modal.dataset.searchSuggestions || 'Suggestions',
      viewAll: modal.dataset.searchViewAll || 'View all results',
      recent: modal.dataset.searchRecent || 'Recent searches',
      soldOut: modal.dataset.searchSoldOut || 'Sold out'
    };

    const recentKey = 'pooji-recent-searches';
    const recentWrap = qs('[data-search-recent-wrap]', modal);
    const recentList = qs('[data-search-recent-list]', modal);

    function getRecent() {
      try {
        const parsed = JSON.parse(localStorage.getItem(recentKey) || '[]');
        return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
      } catch (err) {
        return [];
      }
    }

    function saveRecent(query) {
      const term = String(query || '').trim();
      if (term.length < 2) return;
      const next = [term, ...getRecent().filter((item) => item.toLowerCase() !== term.toLowerCase())].slice(0, 5);
      try {
        localStorage.setItem(recentKey, JSON.stringify(next));
      } catch (err) {
        /* ignore quota / private mode */
      }
    }

    function renderRecent() {
      if (!recentWrap || !recentList) return;
      const items = getRecent();
      if (!items.length) {
        recentWrap.hidden = true;
        recentList.innerHTML = '';
        return;
      }
      recentList.innerHTML = items
        .map(
          (term) =>
            `<button type="button" class="search-modal__chip" data-search-suggestion="${escapeHtml(term)}">${escapeHtml(term)}</button>`
        )
        .join('');
      recentWrap.hidden = false;
    }

    let timer;
    let requestId = 0;

    function hideEmpty() {
      if (!empty) return;
      empty.hidden = true;
      if (emptyMessage) emptyMessage.textContent = '';
    }

    function showIdle() {
      renderRecent();
      idle.hidden = false;
      status.hidden = true;
      results.hidden = true;
      results.innerHTML = '';
      hideEmpty();
    }

    function showStatus(message) {
      idle.hidden = true;
      results.hidden = true;
      results.innerHTML = '';
      hideEmpty();
      status.hidden = false;
      status.textContent = message;
    }

    function showNoResults(query) {
      idle.hidden = true;
      status.hidden = true;
      results.hidden = true;
      results.innerHTML = '';
      if (!empty) {
        showStatus(strings.noResults.replace('[[terms]]', query));
        return;
      }
      if (emptyMessage) {
        emptyMessage.textContent = strings.noResults.replace('[[terms]]', query);
      }
      if (emptyChips && popularChips) {
        emptyChips.innerHTML = popularChips.innerHTML;
      }
      empty.hidden = false;
    }

    function productPrice(product) {
      const price = formatMoney(product.price);
      if (toMajorUnits(product.compare_at_price) > toMajorUnits(product.price)) {
        return `${price} <span class="text-muted"><s>${formatMoney(product.compare_at_price)}</s></span>`;
      }
      return price;
    }

    function renderResults(payload, query) {
      const data = payload?.resources?.results || {};
      const products = data.products || [];
      const collections = data.collections || [];
      const queries = data.queries || [];
      const hasResults = products.length || collections.length || queries.length;

      if (!hasResults) {
        showNoResults(query);
        return;
      }

      let html = '';

      if (queries.length) {
        html += `<div class="search-modal__section"><p class="search-modal__section-title">${escapeHtml(strings.suggestions)}</p><ul class="search-modal__list">`;
        queries.forEach((item) => {
          html += `<li><a class="search-modal__link-row" href="${escapeHtml(item.url)}"><span>${escapeHtml(item.text)}</span><span aria-hidden="true">→</span></a></li>`;
        });
        html += '</ul></div>';
      }

      if (products.length) {
        html += `<div class="search-modal__section"><p class="search-modal__section-title">${escapeHtml(strings.products)}</p><ul class="search-modal__list">`;
        products.forEach((product) => {
          const imageUrl = product.featured_image?.url;
          const image = imageUrl
            ? `<img src="${escapeHtml(imageUrl)}${imageUrl.includes('?') ? '&' : '?'}width=128" alt="" width="64" height="64" loading="lazy">`
            : '';
          html += `<li>
            <a class="search-modal__product" href="${escapeHtml(product.url)}">
              <span class="search-modal__product-media">${image}</span>
              <span>
                <p class="search-modal__product-title">${escapeHtml(product.title)}</p>
                <p class="search-modal__product-price">${productPrice(product)}</p>
                ${product.available === false ? `<p class="search-modal__product-soldout">${escapeHtml(strings.soldOut)}</p>` : ''}
              </span>
              <span class="search-modal__product-arrow" aria-hidden="true">→</span>
            </a>
          </li>`;
        });
        html += '</ul></div>';
      }

      if (collections.length) {
        html += `<div class="search-modal__section"><p class="search-modal__section-title">${escapeHtml(strings.collections)}</p><ul class="search-modal__list">`;
        collections.forEach((collection) => {
          html += `<li><a class="search-modal__link-row" href="${escapeHtml(collection.url)}"><span>${escapeHtml(collection.title)}</span><span aria-hidden="true">→</span></a></li>`;
        });
        html += '</ul></div>';
      }

      const viewUrl = `${settings.routes.search}?q=${encodeURIComponent(query)}&type=product`;
      html += `<a class="search-modal__view-all" href="${escapeHtml(viewUrl)}">${escapeHtml(strings.viewAll)}</a>`;

      idle.hidden = true;
      status.hidden = true;
      hideEmpty();
      results.hidden = false;
      results.innerHTML = html;
    }

    async function runSearch(query) {
      const q = query.trim();
      clearBtn.hidden = !q;
      if (q.length < 2) {
        showIdle();
        return;
      }

      const current = ++requestId;
      showStatus(strings.loading);

      try {
        const [suggestData, localeProducts] = await Promise.all([
          fetchPredictiveResults(predictiveEndpointForLocale(), q, 8),
          fetchLocaleSearchProducts(q)
        ]);
        const data = applyLocaleProductTitles(
          mergePredictivePayload(emptyPredictivePayload(), suggestData),
          localeProducts
        );
        if (current !== requestId) return;
        renderResults(data, q);
      } catch (err) {
        if (current !== requestId) return;
        showNoResults(q);
        console.error(err);
      }
    }

    const form = qs('[data-search-form]', modal);
    form?.addEventListener('submit', () => saveRecent(input.value));

    results.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;
      saveRecent(input.value);
    });

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => runSearch(input.value), 220);
    });

    clearBtn?.addEventListener('click', () => {
      input.value = '';
      clearBtn.hidden = true;
      showIdle();
      input.focus();
    });

    modal.addEventListener('click', (e) => {
      const suggestion = e.target.closest('[data-search-suggestion]');
      if (!suggestion) return;
      input.value = suggestion.getAttribute('data-search-suggestion');
      saveRecent(input.value);
      runSearch(input.value);
      input.focus();
    });

    const observer = new MutationObserver(() => {
      if (modal.classList.contains('is-open')) {
        const currentQuery = new URLSearchParams(window.location.search).get('q');
        if (currentQuery && !input.value) input.value = currentQuery;
        clearBtn.hidden = !input.value;
        renderRecent();
        requestAnimationFrame(() => {
          input.focus();
          if (input.value) input.select();
        });
      }
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
    renderRecent();
  }

  function initSearchFallback() {
    const wrap = qs('[data-search-fallback]');
    if (!wrap) return;
    const query = wrap.getAttribute('data-search-fallback-query') || '';
    const grid = qs('[data-search-fallback-grid]', wrap);
    const countEl = qs('[data-search-fallback-count]', wrap);
    const emptyState = qs('[data-search-empty-state]');
    if (!grid || !query || !isArabicLocale()) return;
    const primaryEndpoint = predictiveEndpointForLocale() || settings.routes.predictiveSearchPrimary;
    if (!primaryEndpoint) return;

    fetchPredictiveResults(primaryEndpoint, query, 24)
      .then((payload) => {
        const products = payload?.resources?.results?.products || [];
        if (!products.length) return;
        grid.innerHTML = products
          .map((product) => {
            const href = escapeHtml(withStoreLocalePath(product.url));
            const imageUrl = product.featured_image?.url;
            const image = imageUrl
              ? `<img class="product-card__image" src="${escapeHtml(imageUrl)}${imageUrl.includes('?') ? '&' : '?'}width=480" alt="${escapeHtml(product.title)}" width="480" height="480" loading="lazy">`
              : '';
            return `<article class="product-card">
              <a class="product-card__image-link media media--square" href="${href}">${image}</a>
              <div class="product-card__info">
                <p class="product-card__title"><a href="${href}">${escapeHtml(product.title)}</a></p>
                <div class="product-card__meta"><div class="price"><span class="price__regular">${formatMoney(product.price)}</span></div></div>
              </div>
            </article>`;
          })
          .join('');
        if (countEl) countEl.textContent = String(products.length);
        wrap.hidden = false;
        if (emptyState) emptyState.hidden = true;
      })
      .catch((err) => console.error(err));
  }

  function initSearchSort() {
    const select = qs('[data-search-sort]');
    if (!select || !select.form) return;
    select.addEventListener('change', () => select.form.submit());
  }

  /* ---------- hero slider ---------- */
  function markHeroLcpReady() {
    const run = () => {
      qsa('[data-hero-slider], .hero-home').forEach((root) => {
        root.classList.remove('is-booting');
        root.classList.add('is-lcp-ready');
      });
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(run));
    } else {
      setTimeout(run, 50);
    }
  }

  function initHeroSliders() {
    qsa('[data-hero-slider]').forEach((root) => {
      const slides = qsa('[data-hero-slide]', root);
      if (slides.length < 2) return;

      const dots = qsa('[data-hero-dot]', root);
      const prev = qs('[data-hero-prev]', root);
      const next = qs('[data-hero-next]', root);
      const indexCurrent = qs('[data-hero-index-current]', root);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const autoplayEnabled = root.dataset.autoplay === 'true' && !reduceMotion;
      const speed = Math.max(3000, Number(root.dataset.autoplaySpeed || 6000));
      let index = Math.max(
        0,
        slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      let timer = null;
      let touchStartX = 0;
      let touchStartY = 0;
      let touchDeltaX = 0;
      let touchDeltaY = 0;
      let swiping = false;

      function pad(n) {
        return String(n).padStart(2, '0');
      }

      function restartMeterAnimation(activeDot) {
        if (!activeDot) return;
        const fill = qs('.hero-slider__meter-fill', activeDot);
        if (!fill) return;
        fill.style.animation = 'none';
        // force reflow so the fill animation restarts cleanly
        void fill.offsetWidth;
        fill.style.animation = '';
      }

      function goTo(nextIndex, { user = false } = {}) {
        const total = slides.length;
        index = ((nextIndex % total) + total) % total;
        slides.forEach((slide, i) => {
          const active = i === index;
          slide.classList.toggle('is-active', active);
          slide.classList.remove('is-initial');
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          if (active) slide.removeAttribute('inert');
          else slide.setAttribute('inert', '');

          // restart image ken-burns on the newly active slide
          if (active) {
            const img = qs('.hero-home__image', slide);
            if (img) {
              img.style.animation = 'none';
              void img.offsetWidth;
              img.style.animation = '';
            }
          }
        });
        dots.forEach((dot, i) => {
          const active = i === index;
          dot.classList.toggle('is-active', active);
          dot.classList.toggle('is-complete', i < index);
          dot.setAttribute('aria-selected', active ? 'true' : 'false');
          if (active) restartMeterAnimation(dot);
        });
        if (indexCurrent) indexCurrent.textContent = pad(index + 1);
        if (user) restart();
        else syncAutoplayClass();
      }

      function nextSlide() {
        goTo(index + 1);
      }

      function syncAutoplayClass() {
        root.classList.toggle('is-autoplaying', Boolean(timer));
        root.classList.toggle('is-paused', autoplayEnabled && !timer);
      }

      function stop() {
        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
        syncAutoplayClass();
      }

      function start() {
        if (!autoplayEnabled) {
          syncAutoplayClass();
          return;
        }
        stop();
        timer = window.setInterval(nextSlide, speed);
        syncAutoplayClass();
        const activeDot = dots[index];
        if (activeDot) restartMeterAnimation(activeDot);
      }

      function restart() {
        stop();
        start();
      }

      if (prev) prev.addEventListener('click', () => goTo(index - 1, { user: true }));
      if (next) next.addEventListener('click', () => goTo(index + 1, { user: true }));
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const i = Number(dot.dataset.index || 0);
          goTo(i, { user: true });
        });
      });

      root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goTo(index - 1, { user: true });
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          goTo(index + 1, { user: true });
        }
      });

      root.addEventListener(
        'touchstart',
        (e) => {
          const touch = e.changedTouches[0];
          touchStartX = touch?.clientX || 0;
          touchStartY = touch?.clientY || 0;
          touchDeltaX = 0;
          touchDeltaY = 0;
          swiping = false;
        },
        { passive: true }
      );
      root.addEventListener(
        'touchmove',
        (e) => {
          const touch = e.changedTouches[0];
          touchDeltaX = (touch?.clientX || 0) - touchStartX;
          touchDeltaY = (touch?.clientY || 0) - touchStartY;
          // Only treat as a slide swipe when horizontal intent is clear
          if (
            !swiping &&
            Math.abs(touchDeltaX) > 14 &&
            Math.abs(touchDeltaX) > Math.abs(touchDeltaY) * 1.25
          ) {
            swiping = true;
            stop();
          }
        },
        { passive: true }
      );
      root.addEventListener(
        'touchend',
        () => {
          if (swiping && Math.abs(touchDeltaX) > 48) {
            if (touchDeltaX < 0) goTo(index + 1, { user: true });
            else goTo(index - 1, { user: true });
          } else if (swiping) {
            start();
          }
          swiping = false;
          touchDeltaX = 0;
          touchDeltaY = 0;
        },
        { passive: true }
      );

      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', (e) => {
        if (!root.contains(e.relatedTarget)) start();
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop();
        else start();
      });

      goTo(index);
      start();
    });
  }

  /* ---------- product recommendations ---------- */
  async function initProductRecommendations() {
    const roots = qsa('[data-related-products][data-url]');
    await Promise.all(
      roots.map(async (root) => {
        const url = root.dataset.url;
        if (!url) return;
        try {
          const res = await fetch(url);
          if (!res.ok) return;
          const text = await res.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const next = doc.querySelector('[data-related-products]');
          if (!next) return;
          const hasGrid = next.querySelector('[data-related-grid]');
          if (hasGrid) {
            root.replaceWith(next);
            initAnimations();
          } else if (!root.querySelector('[data-related-grid]')) {
            root.remove();
          }
        } catch (err) {
          /* keep fallback content */
        }
      })
    );
  }

  /* ---------- carousel buttons ---------- */
  function initCarousels() {
    qsa('[data-carousel]').forEach((carousel) => {
      const track = qs('[data-carousel-track]', carousel);
      const prev = qs('[data-carousel-prev]', carousel);
      const next = qs('[data-carousel-next]', carousel);
      if (!track) return;
      const scrollBy = () => track.clientWidth * 0.8;
      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -scrollBy(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: scrollBy(), behavior: 'smooth' }));
    });
  }

  /* ---------- viewing count (optional, tasteful) ---------- */
  function initViewingCount() {
    qsa('[data-viewing-count]').forEach((el) => {
      const min = 2;
      const max = 8;
      const n = Math.floor(Math.random() * (max - min + 1)) + min;
      el.textContent = String(n);
    });
  }

  function initCollectionAbout() {
    qsa('[data-collection-about]').forEach((root) => {
      const content = qs('[data-collection-about-content]', root);
      const btn = qs('[data-collection-about-toggle]', root);
      if (!content || !btn) return;

      const clampedHeight = content.clientHeight;
      const prevDisplay = content.style.display;
      const prevClamp = content.style.webkitLineClamp;
      const prevOverflow = content.style.overflow;
      content.style.display = 'block';
      content.style.webkitLineClamp = 'unset';
      content.style.overflow = 'visible';
      const fullHeight = content.scrollHeight;
      content.style.display = prevDisplay;
      content.style.webkitLineClamp = prevClamp;
      content.style.overflow = prevOverflow;

      if (fullHeight <= clampedHeight + 8) {
        btn.hidden = true;
        root.classList.add('is-short');
        return;
      }
      btn.hidden = false;
      btn.addEventListener('click', () => {
        const open = root.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
      });
    });
  }

  /* ---------- frequently bought together ---------- */
  function syncFbtMainVariant(variant) {
    if (!variant) return;
    qsa('[data-fbt-main]').forEach((item) => {
      item.dataset.variantId = String(variant.id || '');
      item.dataset.price = String(variant.price || 0);
      item.dataset.available = variant.available === false ? 'false' : 'true';
      const price = qs('[data-fbt-item-price]', item);
      if (price && variant.price_money) price.textContent = variant.price_money;
      item.closest('[data-fbt]')?.dispatchEvent(new Event('fbt:refresh'));
    });
  }

  let comboRequestId = 0;
  let comboNotesDraft = {
    card: '',
    cake: '',
    deliveryDate: '',
    deliveryTime: '',
  };

  function readFormProperties(form) {
    const properties = {};
    if (!form) return properties;
    new FormData(form).forEach((value, key) => {
      const match = String(key).match(/^properties\[(.+)\]$/);
      if (!match || typeof value !== 'string' || !value.trim()) return;
      properties[match[1]] = value.trim();
    });
    return properties;
  }

  function comboSourceForm() {
    const quickView = qs('#QuickViewModal');
    if (quickView?.classList.contains('is-open')) {
      return qs('[data-quick-view-form], [data-product-form]', quickView) || qs('#ProductForm');
    }
    return qs('#ProductForm');
  }

  function snapshotComboNotes(form) {
    const source = form || comboSourceForm();
    const properties = readFormProperties(source);
    comboNotesDraft = {
      card: properties['Card message'] || properties['Gift message'] || '',
      cake: properties['Cake message'] || '',
      deliveryDate: properties['Delivery date'] || '',
      deliveryTime: properties['Delivery time'] || '',
    };
  }

  function comboCakeSelected(root) {
    const cakeSlot = qs('[data-fbt-slot="cake"]', root);
    if (!cakeSlot) return false;
    return Boolean(qs('[data-fbt-select]', cakeSlot)?.checked);
  }

  function syncComboCakeNote(root) {
    const wrap = qs('[data-combo-cake-note]', root);
    if (!wrap) return;
    const cakeSlot = qs('[data-fbt-slot="cake"]', root);
    wrap.hidden = !(comboCakeSelected(root) || (!cakeSlot && Boolean(comboNotesDraft.cake)));
  }

  function hydrateComboNotes(root) {
    if (!root) return;
    const card = qs('[data-combo-prop="Card message"]', root);
    const cake = qs('[data-combo-prop="Cake message"]', root);
    if (card) card.value = comboNotesDraft.card || '';
    if (cake) cake.value = comboNotesDraft.cake || '';
    syncComboCakeNote(root);
  }

  function readComboNoteFields(root) {
    return {
      card: (qs('[data-combo-prop="Card message"]', root)?.value || '').trim(),
      cake: (qs('[data-combo-prop="Cake message"]', root)?.value || '').trim(),
    };
  }

  function currentProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function isCurrentProductHandle(handle) {
    return Boolean(handle) && currentProductHandle() === handle;
  }

  function nativeComboSheet() {
    return qsa('#ProductBundleSheet, #ProductBundleSheetNative').find(
      (sheet) => sheet.dataset.comboSource !== 'remote'
    );
  }

  function stashNativeComboSheet() {
    const native = nativeComboSheet();
    if (!native || native.id === 'ProductBundleSheetNative') return;
    native.dataset.comboSource = native.dataset.comboSource || 'page';
    native.id = 'ProductBundleSheetNative';
  }

  function restoreNativeComboSheet() {
    const remote = qs('#ProductBundleSheet[data-combo-source="remote"]');
    if (remote?.classList.contains('is-open')) return;
    if (remote) remote.remove();
    const native = qs('#ProductBundleSheetNative');
    if (native) native.id = 'ProductBundleSheet';
  }

  function openProductBundleSheet() {
    const sheet = qs('#ProductBundleSheet');
    if (!sheet) return;
    if (sheet.parentElement !== document.body) {
      document.body.appendChild(sheet);
    }
    hydrateComboNotes(qs('[data-fbt]', sheet));
    openOverlay(sheet);
  }

  function comboSectionUrl(handle, variantId) {
    const root = (settings.routes?.root || '/').replace(/\/$/, '');
    const params = new URLSearchParams();
    if (variantId) params.set('variant', String(variantId));
    params.set('section_id', 'product-combo-sheet');
    return `${root}/products/${encodeURIComponent(handle)}?${params}`;
  }

  async function fetchComboSheetHtml(handle, variantId) {
    const res = await fetch(comboSectionUrl(handle, variantId));
    if (res.ok) return res.text();
    const root = (settings.routes?.root || '/').replace(/\/$/, '');
    const params = new URLSearchParams();
    if (variantId) params.set('variant', String(variantId));
    params.set('sections', 'product-combo-sheet');
    const fallback = await fetch(`${root}/products/${encodeURIComponent(handle)}?${params}`);
    if (!fallback.ok) throw new Error('Unable to load combo');
    const json = await fallback.json();
    return json['product-combo-sheet'] || '';
  }

  function setComboLoaderMessage(message) {
    const loader = qs('#ProductComboLoader');
    const body = loader && qs('[data-combo-loader-body]', loader);
    if (!body) return;
    body.innerHTML = `<p class="gift-sheet__loading">${message}</p>`;
  }

  async function loadRemoteComboSheet(handle, variantId) {
    const requestId = ++comboRequestId;
    snapshotComboNotes();
    const existing = qs('#ProductBundleSheet[data-combo-source="remote"]');
    if (existing && existing.dataset.comboHandle === handle && qs('[data-fbt]', existing)) {
      snapshotComboNotes();
      const quickView = qs('#QuickViewModal');
      if (quickView?.classList.contains('is-open')) closeOverlay(quickView);
      hydrateComboNotes(qs('[data-fbt]', existing));
      openOverlay(existing);
      return;
    }

    const loader = qs('#ProductComboLoader');
    const loadingLabel = window.theme?.strings?.loading || 'Loading…';
    if (loader) {
      snapshotComboNotes();
      setComboLoaderMessage(loadingLabel);
      const quickView = qs('#QuickViewModal');
      if (quickView?.classList.contains('is-open')) closeOverlay(quickView);
      openOverlay(loader);
    }

    try {
      const html = await fetchComboSheetHtml(handle, variantId);
      if (requestId !== comboRequestId) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-product-combo-sheet]');
      const sheet = content?.querySelector('#ProductBundleSheet');
      if (!sheet) throw new Error('Unable to load combo');

      stashNativeComboSheet();
      existing?.remove();
      sheet.dataset.comboSource = 'remote';
      sheet.dataset.comboHandle = handle;
      document.body.appendChild(sheet);
      initFrequentlyBoughtTogether();
      hydrateComboNotes(qs('[data-fbt]', sheet));
      openOverlay(sheet);
    } catch (err) {
      if (requestId !== comboRequestId) return;
      const loaderEl = qs('#ProductComboLoader');
      const errorLabel = loaderEl?.dataset.comboError || 'Couldn’t load this combo. Try again.';
      if (loaderEl) {
        setComboLoaderMessage(errorLabel);
        if (!loaderEl.classList.contains('is-open')) openOverlay(loaderEl);
      } else {
        alert(errorLabel);
      }
    }
  }

  async function openComboForHandle(handle, variantId) {
    if (!handle) return;
    const native = nativeComboSheet();
    if (native && isCurrentProductHandle(handle)) {
      if (native.id === 'ProductBundleSheetNative') native.id = 'ProductBundleSheet';
      qs('#ProductBundleSheet[data-combo-source="remote"]')?.remove();
      snapshotComboNotes();
      const quickView = qs('#QuickViewModal');
      if (quickView?.classList.contains('is-open')) closeOverlay(quickView);
      openProductBundleSheet();
      return;
    }
    await loadRemoteComboSheet(handle, variantId);
  }

  function initBundleNudge() {
    document.addEventListener('click', (event) => {
      const comboTrigger = event.target.closest('[data-combo-sheet], .qv__combo');
      if (comboTrigger) {
        const handle = comboTrigger.dataset.comboSheet || comboTrigger.dataset.productHandle;
        if (!handle) return;
        event.preventDefault();
        openComboForHandle(handle, comboTrigger.dataset.comboVariant);
        return;
      }

      const trigger = event.target.closest('[data-bundle-nudge], [data-open="#ProductBundleSheet"]');
      if (!trigger) return;
      event.preventDefault();
      snapshotComboNotes();
      openProductBundleSheet();
    });

    const hash = window.location.hash;
    if (hash === '#ProductBundle' || hash === '#ProductBundleSheet') {
      snapshotComboNotes();
      openProductBundleSheet();
    }
  }

  function initFrequentlyBoughtTogether() {
    qsa('[data-fbt]').forEach((root) => {
      if (root.dataset.fbtReady === 'true') return;
      root.dataset.fbtReady = 'true';

      const button = qs('[data-fbt-add]', root);
      const totalEl = qs('[data-fbt-total]', root);
      const countEl = qs('[data-fbt-count]', root);
      const status = qs('[data-fbt-status]', root);
      const moneyFormat = root.dataset.moneyFormat || settings.moneyFormat;

      const selectedItems = () =>
        qsa('[data-fbt-item]', root).filter((item) => {
          if (item.hasAttribute('data-fbt-main')) return true;
          return Boolean(qs('[data-fbt-select]', item)?.checked);
        });

      const refresh = () => {
        const items = selectedItems();
        const total = items.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);
        const main = qs('[data-fbt-main]', root);
        const unavailable = !items.length || main?.dataset.available === 'false';
        if (totalEl) totalEl.textContent = formatMoney(total, moneyFormat);
        if (countEl) {
          const template =
            items.length === 1
              ? root.dataset.countOne || '1 item selected'
              : root.dataset.countOther || '[[count]] items selected';
          countEl.textContent = template.replace('[[count]]', String(items.length));
        }
        if (button) button.disabled = unavailable;
        syncComboCakeNote(root);
      };

      const collectCartItems = () => {
        const sheet = root.closest('#ProductBundleSheet') || qs('#ProductBundleSheet');
        const form = qs('#ProductForm');
        const useForm = Boolean(form) && sheet?.dataset.comboSource !== 'remote';
        if (useForm && typeof form.reportValidity === 'function' && !form.reportValidity()) {
          if (sheet?.classList.contains('is-open')) closeOverlay(sheet);
          return null;
        }

        const formData = useForm ? new FormData(form) : null;
        const formProperties = useForm ? readFormProperties(form) : {};
        const notes = readComboNoteFields(root);
        const cardMessage = notes.card || comboNotesDraft.card;
        const cakeMessage = notes.cake || comboNotesDraft.cake;
        const selected = selectedItems();
        const main = qs('[data-fbt-main]', root);

        const items = selected.map((item) => {
          const isMain = item === main;
          const isCake = item.dataset.fbtSlot === 'cake';
          const cartItem = {
            id: Number(
              isMain && formData ? formData.get('id') || item.dataset.variantId : item.dataset.variantId
            ),
            quantity: isMain && formData ? Math.max(1, Number(formData.get('quantity') || 1)) : 1,
          };

          const properties = {};
          if (isMain) {
            Object.keys(formProperties).forEach((key) => {
              if (key === 'Card message' || key === 'Gift message' || key === 'Cake message') return;
              properties[key] = formProperties[key];
            });
            if (comboNotesDraft.deliveryDate) properties['Delivery date'] = comboNotesDraft.deliveryDate;
            if (comboNotesDraft.deliveryTime) properties['Delivery time'] = comboNotesDraft.deliveryTime;
            if (cardMessage) properties['Card message'] = cardMessage;
            const cakeInCombo = selected.some((entry) => entry.dataset.fbtSlot === 'cake');
            if (cakeMessage && !cakeInCombo) properties['Cake message'] = cakeMessage;
          }
          if (isCake && cakeMessage) properties['Cake message'] = cakeMessage;
          if (Object.keys(properties).length) cartItem.properties = properties;
          return cartItem;
        });

        if (!items.length || items.some((item) => !item.id)) return null;
        comboNotesDraft.card = cardMessage;
        comboNotesDraft.cake = cakeMessage;
        return items;
      };

      const submitBundle = async () => {
        const items = collectCartItems();
        if (!items || !button) return;
        if (status) status.textContent = '';
        button.disabled = true;
        setAtcState(button, 'loading');
        try {
          await addItemsToCart(items, button, root.dataset.addedLabel);
          if (status) status.textContent = root.dataset.addedLabel || '';
        } catch (err) {
          if (status) status.textContent = err.message;
          else alert(err.message);
          setAtcState(button, 'idle');
        } finally {
          window.setTimeout(() => {
            setAtcState(button, 'idle');
            refresh();
          }, prefersReducedMotion() ? 0 : 900);
        }
      };

      qsa('[data-fbt-select]', root).forEach((input) => {
        input.addEventListener('change', refresh);
      });
      root.addEventListener('fbt:refresh', refresh);

      button?.addEventListener('click', submitBundle);

      refresh();
    });
  }

  /* ---------- product gift addon tabs + sheet ---------- */
  function initProductAddons() {
    qsa('[data-product-addons]').forEach((root) => {
      const tabs = qsa('[data-addon-tab]', root);
      const panels = qsa('[data-addon-panel]', root);
      if (tabs.length < 2) return;

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          tabs.forEach((item, i) => {
            const active = i === index;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          panels.forEach((panel, i) => {
            const active = i === index;
            panel.classList.toggle('is-active', active);
            if (active) panel.removeAttribute('hidden');
            else panel.setAttribute('hidden', '');
          });
        });
      });
    });
  }

  function configureBundlePicker(sheet) {
    const body = qs('[data-gift-sheet-body]', sheet);
    if (!body || !sheet.dataset.fbtTarget) return;
    const chooseLabel = window.theme?.strings?.bundleChoose || 'Choose';
    qsa('[data-gift-addon]', body).forEach((card) => {
      card.classList.remove('is-in-cart');
      const actions = qs('[data-gift-addon-actions]', card);
      if (!actions) return;
      actions.innerHTML = `
        <button type="button" class="button button--secondary gift-addon__btn" data-fbt-choose>
          <span class="gift-addon__btn-label">${chooseLabel}</span>
        </button>
      `;
    });
  }

  function chooseBundleAddon(sheet, card) {
    const root = qs('[data-fbt]', qs('#ProductBundleSheet')) || qs('[data-fbt]');
    if (!root || !sheet || !card) return;
    const target = sheet?.dataset.fbtTarget;
    const slot = qsa('[data-fbt-slot]', root).find((item) => item.dataset.fbtSlot === target);
    if (!slot) return;

    slot.dataset.variantId = card.dataset.variantId || '';
    slot.dataset.price = card.dataset.price || '0';
    slot.dataset.available = 'true';

    const title = qs('.fbt-item__title', slot);
    const price = qs('[data-fbt-item-price]', slot);
    const image = qs('.fbt-item__media img', slot);
    if (title) title.textContent = card.dataset.productTitle || '';
    if (price) price.textContent = card.dataset.priceMoney || '';
    if (image && card.dataset.image) {
      image.src = card.dataset.image;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.alt = '';
    }

    const input = qs('[data-fbt-select]', slot);
    if (input) input.checked = true;
    root.dispatchEvent(new Event('fbt:refresh'));
    closeOverlay(sheet);
  }

  async function openGiftAddonSheet(collectionUrl, title, options = {}) {
    const sheet = qs('#GiftAddonSheet');
    if (!sheet || !collectionUrl) return;
    const body = qs('[data-gift-sheet-body]', sheet);
    const titleEl = qs('[data-gift-sheet-title]', sheet);
    const eyebrowEl = qs('[data-gift-sheet-eyebrow]', sheet);
    const loadingLabel = window.theme?.strings?.loading || 'Loading…';
    const target = options.target || '';

    sheet.dataset.fbtTarget = target;
    sheet.classList.toggle('is-bundle-picker', Boolean(target));
    if (eyebrowEl) {
      eyebrowEl.textContent = target
        ? window.theme?.strings?.bundleCustomize || 'Customize your gift combo'
        : window.theme?.strings?.addOns || 'Add extras';
    }
    if (titleEl) {
      const chooseTemplate = window.theme?.strings?.bundleChooseCategory || 'Choose [[category]]';
      titleEl.textContent = target
        ? chooseTemplate.replace('[[category]]', title || '')
        : title || window.theme?.strings?.addOns || 'Add extras';
    }
    if (body) body.innerHTML = `<p class="gift-sheet__loading">${loadingLabel}</p>`;
    openOverlay(sheet);

    try {
      const url = new URL(collectionUrl, window.location.origin);
      url.searchParams.set('section_id', 'gift-addon-browser');
      const res = await fetch(url.pathname + url.search);
      if (!res.ok) throw new Error('Unable to load collection');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-gift-browser]');
      if (!body) return;
      if (content) {
        body.innerHTML = content.innerHTML;
        if (target) configureBundlePicker(sheet);
        else await syncGiftAddonQuantities();
      } else {
        body.innerHTML = '<p class="gift-sheet__loading">Unable to load products.</p>';
      }
    } catch (err) {
      if (body) body.innerHTML = `<p class="gift-sheet__loading">${err.message || 'Unable to load products.'}</p>`;
    }
  }

  document.addEventListener('click', (e) => {
    const sheet = e.target.closest('#GiftAddonSheet');
    const pickerCard = e.target.closest('[data-gift-addon]');
    if (
      sheet?.dataset.fbtTarget &&
      pickerCard &&
      e.target.closest('[data-fbt-choose], .gift-addon__media, .gift-addon__title a')
    ) {
      e.preventDefault();
      chooseBundleAddon(sheet, pickerCard);
      return;
    }

    const trigger = e.target.closest('[data-gift-sheet-open], [data-fbt-change]');
    if (!trigger) return;
    e.preventDefault();
    openGiftAddonSheet(trigger.dataset.collectionUrl, trigger.dataset.collectionTitle, {
      target: trigger.dataset.fbtTarget || '',
    });
  });

  /* ---------- boot ---------- */
  function whenIdle(fn, timeout) {
    const maxWait = timeout == null ? 1200 : timeout;
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => fn(), { timeout: maxWait });
    } else {
      setTimeout(fn, 1);
    }
  }

  function boot() {
    initHeader();
    initHeaderNav();
    initAccountMenu();
    initMobileAccordions();
    initHeroSliders();
    markHeroLcpReady();
    initAnimations();
    initAppBlockCollapse();
    updateCartCount();

    const needsProductUi = Boolean(
      qs('[data-product-form], [data-variant-picker], [data-sticky-atc], [data-delivery-picker]')
    );
    initQuantityButtons();
    if (needsProductUi) {
      initVariantCards();
      initVariantPickers();
      initCharCounters();
      initStickyAtc();
      initFrequentlyBoughtTogether();
      initBundleNudge();
      initProductAddons();
      initDeliveryPickers();
      ensureDefaultDelivery();
    }
    if (qs('[data-card-swatches]')) initCardSwatches();
    initRecipientShop();

    whenIdle(() => {
      initPredictiveSearch();
      initSearchFallback();
      initSearchSort();
      initGalleries();
      initCarousels();
      initProductRecommendations();
      initViewingCount();
      initCollectionAbout();
      updateCountdowns();
      setInterval(updateCountdowns, 15000);
      if (!needsProductUi) {
        initVariantCards();
        initVariantPickers();
        initQuantityButtons();
        initCharCounters();
        initStickyAtc();
        initProductAddons();
        initDeliveryPickers();
        ensureDefaultDelivery();
      }
      initCardSwatches();
      initRecipientShop();
    });
  }

  function pinRecipientTabs(root) {
    const tabbar = qs('[data-recipient-tabbar]', root);
    const slot = qs('[data-recipient-tab-slot]', root);
    const header = qs('[data-site-header]');
    const headerGroup = header?.closest('.shopify-section-group-header-group') || header;
    if (!tabbar || !slot || !headerGroup) return;

    let pinHost = document.getElementById('RecipientTabPin');
    if (!pinHost) {
      pinHost = document.createElement('div');
      pinHost.id = 'RecipientTabPin';
      pinHost.className = 'recipient-shop__header-pin';
      pinHost.hidden = true;
      pinHost.innerHTML = '<div class="page-width" data-recipient-pin-inner></div>';
      headerGroup.appendChild(pinHost);
    }
    const pinInner = qs('[data-recipient-pin-inner]', pinHost);
    if (!pinInner) return;

    pinHost.classList.toggle('is-cream', root.classList.contains('section--cream'));

    let pinned = pinInner.contains(tabbar);

    const sync = () => {
      const headerBottom = headerGroup.getBoundingClientRect().bottom;
      const slotTop = slot.getBoundingClientRect().top;
      const rootBottom = root.getBoundingClientRect().bottom;
      const barHeight = tabbar.offsetHeight || slot.offsetHeight;
      const shouldPin = slotTop <= headerBottom + 1 && rootBottom > headerBottom + barHeight + 12;

      if (shouldPin && !pinned) {
        slot.style.height = `${barHeight}px`;
        pinInner.appendChild(tabbar);
        pinHost.hidden = false;
        pinned = true;
      } else if (!shouldPin && pinned) {
        slot.appendChild(tabbar);
        slot.style.height = '';
        pinHost.hidden = true;
        pinned = false;
      }
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        sync();
        ticking = false;
      });
    };

    sync();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  function initRecipientShop() {
    qsa('[data-recipient-shop]').forEach((root) => {
      if (root.dataset.recipientReady === 'true') return;
      root.dataset.recipientReady = 'true';
      pinRecipientTabs(root);
      const tabs = qsa('[data-recipient-tab]', root);
      const panels = qsa('[data-recipient-panel]', root);
      if (tabs.length < 2) return;

      const activate = (index) => {
        tabs.forEach((item, i) => {
          const active = i === index;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel, i) => {
          const active = i === index;
          panel.classList.toggle('is-active', active);
          if (active) panel.removeAttribute('hidden');
          else panel.setAttribute('hidden', '');
        });
        const activeTab = tabs[index];
        const scroller = activeTab?.closest('.recipient-shop__tabs');
        if (activeTab && scroller) {
          const left = activeTab.offsetLeft - (scroller.clientWidth - activeTab.offsetWidth) / 2;
          scroller.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
        }
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(index));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const dir = event.key === 'ArrowRight' ? 1 : -1;
          const next = (index + dir + tabs.length) % tabs.length;
          tabs[next].focus();
          activate(next);
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  theme.openOverlay = openOverlay;
  theme.closeOverlay = closeOverlay;
  theme.refreshCartDrawer = refreshCartDrawer;
  theme.formatMoney = formatMoney;
  window.theme = theme;
})();
